import { publicBookingEntry } from "../utils/publicBookingEntry";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CalendarClock,
  Car,
  CheckCircle2,
  CreditCard,
  Crown,
  MapPin,
  MessageCircle,
  Minus,
  Plane,
  Plus,
  ShieldCheck,
  Sparkles,
  UserRound,
  WalletCards,
  X,
} from "lucide-react";
import { API_BASE_URL as API } from "../apiConfig";
import RetailMemberAuthPanel, { phoneCountryOptions } from "../components/retail/RetailMemberAuthPanel";
import RetailWalletSummary from "../components/retail/RetailWalletSummary";
import PublicBrandMark from "../components/public/PublicBrandMark";
import PublicLanguageSwitcher from "../components/public/PublicLanguageSwitcher";
import { OpeningLanguageGate } from "./PublicBookingEntryPage";
import publicBookingChauffeurCharter from "../assets/public-booking-chauffeur-charter.png";
import publicBookingLuxuryFleet from "../assets/public-booking-luxury-fleet.png";
import tripSubtypeArrivalImage from "../assets/trip-subtype-arrival.png";
import tripSubtypeDepartureImage from "../assets/trip-subtype-departure.png";
import tripSubtypePointToPointImage from "../assets/trip-subtype-point-to-point.png";
import vehicleAlphardImage from "../assets/vehicle-alphard.png";
import vehicleHiaceImage from "../assets/vehicle-hiace.png";
import vehiclePremiumHiaceImage from "../assets/vehicle-premium-hiace.png";

const PUBLIC_BOOKING_WHATSAPP_PHONE = process.env.REACT_APP_PUBLIC_BOOKING_WHATSAPP_PHONE || "";
const PUBLIC_FLIGHT_LOOKUP_FALLBACK_API = (
  process.env.REACT_APP_PUBLIC_FLIGHT_LOOKUP_FALLBACK_API || "https://shonanpartner.com/api"
).replace(/\/+$/, "");
const PUBLIC_ADDRESS_PARSE_FALLBACK_API = (
  process.env.REACT_APP_PUBLIC_BOOKING_ADDRESS_PARSE_FALLBACK_API || "https://shonanpartner.com/api"
).replace(/\/+$/, "");
const JAPANESE_POSTAL_CODE_LOOKUP_API = "https://zipcloud.ibsnet.co.jp/api/search";
const PUBLIC_BOOKING_DRAFT_KEY = "public_booking_draft_v2";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("Turnstile unavailable"));
  if (window.turnstile) return Promise.resolve(window.turnstile);

  const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(window.turnstile), { once: true });
      existingScript.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(window.turnstile), { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.appendChild(script);
  });
}

const shouldUseFlightLookupFallback = () => (
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  PUBLIC_FLIGHT_LOOKUP_FALLBACK_API &&
  !API.startsWith(PUBLIC_FLIGHT_LOOKUP_FALLBACK_API)
);

const bookingLanguageOptions = [
  { code: "en", label: "EN" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中国語" },
  { code: "zhHant", label: "繁体字" },
];

const publicBookingCopy = {
  en: {
    metaTitle: "Shonan International Private Transfer Booking",
    eyebrow: "Private Transfer Service in Japan",
    title: "Book your airport transfer or private charter",
    subtitle: "Choose a service to get started — we’ll guide you through each step.",
    steps: ["Service", "Trip", "Vehicle", "Contact", "Review"],
    serviceStep: "Step 1 of 5",
    chooseService: "Choose your service",
    chooseServiceSubtitle: "Choose Airport Transfer or Private Charter to begin.",
    charterTitle: "PRIVATE CHARTER",
    charterDescription: <>Hourly charter<br />Private driver at<br />your service</>,
    charterMeta: "Best for flexibility",
    transferTitle: "AIRPORT TRANSFER",
    transferDescription: <>Point-to-point<br />Airport or city<br />transfer</>,
    transferMeta: "Best for direct trips",
    selected: "Selected",
    choose: "Choose",
    vehicleTitle: "Choose your vehicle",
    vehicleSubtitle: "Capacity is shown clearly so customers choose with confidence.",
    passengers: "Number of passengers",
    luggage: "Number of luggage items",
    childSeats: "Child seats",
    childSeatNote: "First seat free. From the second seat, ¥2,000 each.",
    infantSeats: "Infant Seats",
    infantSeatNote: "¥2,000 per seat, including the first.",
    tripTitleCharter: "Plan the charter route",
    tripTitleTransfer: "Set the trip details",
    tripSubtitle: "We keep this step focused on date, route, and important service details.",
    contactTitle: "Who is traveling?",
    contactSubtitle: "We only need the contact details required to confirm your booking and send the payment link.",
    reviewTitle: "Confirm booking details",
    reviewSubtitle: "Date and time are shown in Japan Standard Time. Please review the details and accept the policies before submitting.",
    serviceTypes: {
      arrival: {
        title: "Airport pickup",
        description: "Passenger arrives at the airport. We meet and transfer to the destination.",
        label: "Airport pickup",
      },
      departure: {
        title: "Airport drop-off",
        description: "Passenger goes to the airport. We suggest pickup timing for the flight.",
        label: "Airport drop-off",
      },
      point_to_point: {
        title: "Point-to-point",
        description: "Hotel, station, port, restaurant, or city-to-city private transfer.",
        label: "Point-to-point transfer",
      },
      fallback: "Airport / point-to-point transfer",
    },
    vehicleMeta: {
      alphard: "Luxury comfort",
      hiace: "Group friendly",
      premium_hiace: "Electric seats · luxury experience",
    },
    vehicleCapacity: {
      alphard: "1-4 passengers",
      hiace: "1-9 passengers",
      premium_hiace: "1-9 passengers",
    },
    vehicleQuote: {
      checking: "Checking",
      pending: "Staff quote",
      waiting: "Enter trip details",
    },
    fields: {
      date: "Departure date",
      time: "Departure time",
      pickupTime: "Departure time",
      charterDepartureArea: "Charter departure area",
      charterCityArea: "Charter city / area",
      pickupLocation: "Pickup location",
      dropoffLocation: "Drop-off location",
      specialLuggage: "Special luggage (optional)",
      charterDetails: "Charter details",
      charterHours: "Charter hours",
      stopovers: "Stopovers",
      overtimeNote: "Overtime note",
      crossAreaNote: "Cross-area note",
      fullName: "Full name",
      email: "Email",
      phone: "Phone",
      remarks: "Remarks",
    },
    placeholders: {
      location: "Hotel, station, port, restaurant, Google Maps link...",
      specialLuggage: "Ski boards, golf bags, stroller...",
      stopovers: "Separate multiple stops with commas",
    },
    notes: {
      overtime: "Overtime fee: Alphard ¥5,000/h, HiAce ¥7,000/h, Premium HiAce ¥10,000/h. Starting or ending outside Tokyo reduces available service time based on the quote rules.",
      crossRegion: "For the destination area, please select the farthest destination in this itinerary.",
      timeAfterFlight: "Adjust after lookup.",
      timeFormat: "24-hour time",
      jst: "Japan Standard Time",
    },
    flight: {
      panelEyebrow: "Flight recognition",
      importDestination: "Import destination airport",
      importArrival: "Import airport pickup details",
      detected: "Detected",
      airportPending: "Airport pending",
      flightNumber: "Flight number",
      example: "Example: NH 879, JL 62, EK 319",
      importing: "Importing...",
      importButton: "Import flight details",
      departureTime: "Flight departure time",
      landingTime: "Flight landing time",
      destinationAirport: "Destination airport",
      pickupAirport: "Pick-up airport",
      empty: "Enter the flight number, then tap Import flight details to load the flight time and airport.",
      checking: "Checking flight information...",
      found: "Flight found",
      verified: "Verified by flight data",
      needsManualCheck: "Manual confirmation needed",
      autoFilled: "Auto-filled",
      airport: "airport",
      flightDate: "flight date",
      pickupTime: "pickup time",
      from: "From",
      to: "To",
      date: "Flight date",
      departure: "Departure time",
      arrival: "Arrival time",
      confirm: "Please confirm this is your correct flight.",
      notConnected: "We could not verify this flight automatically.",
      manualTime: "Please enter the flight time manually.",
      notFound: "Flight information was not found.",
      checkManual: "Please check the flight number, then confirm the time manually.",
      time: "Flight time",
      timePending: "Flight number pending",
      flightTimePending: "flight time pending",
      manualConfirm: "Please confirm manually",
    },
    datePicker: {
      selectTripDate: "Select trip date",
      previousMonth: "Previous month",
      nextMonth: "Next month",
      selectDate: "Select Date",
      selected: "Selected",
      confirm: "Confirm",
    },
    nav: {
      back: "Back",
      continue: "Continue",
      preparing: "Preparing...",
      reviewBooking: "Confirm booking details",
      whatsapp: "Contact us on WhatsApp",
    },
    otherArea: {
      title: "This area needs manual confirmation",
      body: "For Other pick-up or drop-off areas, please contact us on WhatsApp. We will confirm availability and price directly.",
      button: "Open WhatsApp",
    },
    review: {
      cancellationTitle: "Cancellation / refund rule",
      cancellationBody: "Bookings cancelled within 48 hours before service start are not refundable. Unpaid payment links expire after 24 hours.",
      privacy: "I agree to the Privacy Policy",
      terms: "I agree to the Terms of Use",
      cancellation: "I agree to the Cancellation Policy",
      submitting: "Submitting...",
      submit: "Submit booking request",
      rows: {
        serviceType: "Service type",
        dateTime: "Departure date / time",
        route: "Route",
        flight: "Flight",
        vehicle: "Vehicle",
        passengersLuggage: "Passengers / luggage",
        childSeats: "Child seats",
        finalAmount: "Final amount",
      },
      childSeatsValue: "selected · first seat free, from second seat ¥2,000 each",
      pendingQuote: "Pending quote",
    },
    summary: {
      eyebrow: "Concierge summary",
      title: "Your ride",
      service: "Service",
      vehicle: "Vehicle",
      guests: "Guests",
      childSeats: "Child seats",
      tripTime: "Trip time",
      route: "Route",
      flight: "Flight",
      passenger: "Passenger",
      chooseVehicle: "Choose vehicle",
      chooseDateTime: "Choose date and time",
      enterPassenger: "Enter passenger contact",
      estimatedTotal: "Estimated total",
      priceReview: "Price appears on the vehicle step.",
      pricePending: "Price pending confirmation.",
      urgent: "Bookings within 48 hours require staff confirmation before payment.",
      passengerUnit: "passengers",
      luggageUnit: "luggage",
      childSeatValue: "selected · first free, then ¥2,000 each",
    },
    result: {
      received: "Your booking request has been submitted.",
      title: "Save your confirmation code",
      format: "Format: SIC-7KQ9P",
      body: "Please keep this code. You can check your booking with this code and your full name.",
      within48: " Bookings within 48 hours require manual confirmation. We will confirm availability before sending payment.",
      paymentPending: "Payment link pending",
      paymentPendingBody: "Our team will send the secure Square payment link shortly.",
      openPayment: "Continue to payment",
      whatsappPayment: "Send payment link by WhatsApp",
      checkBooking: "Check your booking",
    },
    route: {
      airport: "Airport",
      addPickup: "Add pickup location",
      addDropoff: "Add drop-off location",
      addBoth: "Add pick-up and drop-off",
    },
    recommendation: {
      haneda: "Suggested pickup time: 3 hours before flight departure.",
      itami: "Suggested pickup time: 3 hours before flight departure.",
      kansai: "Suggested pickup time: 3.5 hours before flight departure.",
      narita: "Suggested pickup time: 3.5 hours before flight departure.",
    },
    quoteFallback: "Price will be confirmed by our team. You can still review your request.",
    submitError: "Unable to submit booking request.",
    validation: {
      service: "Please choose a service first.",
      tripDateTime: "Please select the departure date and time.",
      route: "Please enter both pickup and drop-off locations.",
      contact: "Please enter your full name, email, and phone number.",
      member: "Please log in or sign up to continue with Member Booking.",
    },
  },
  ja: {
    metaTitle: "湘南国際｜送迎予約",
    eyebrow: "日本のプライベート送迎サービス",
    title: "空港送迎または貸切チャーターを予約",
    subtitle: "サービスを選択して開始してください。各ステップを順番にご案内します。",
    steps: ["サービス", "行程", "車両", "連絡先", "確認"],
    serviceStep: "ステップ 1 / 5",
    chooseService: "サービスを選択",
    chooseServiceSubtitle: "貸切または送迎を選択してください。",
    charterTitle: "貸切",
    charterDescription: <>時間制貸切<br />専属ドライバーが<br />ご案内します</>,
    charterMeta: "自由な行程に最適",
    transferTitle: "送迎",
    transferDescription: <>市内送迎<br />空港送迎</>,
    transferMeta: "直接移動に最適",
    selected: "選択中",
    choose: "選択",
    vehicleTitle: "車両を選択",
    vehicleSubtitle: "定員を確認しながら、安心して車両をお選びいただけます。",
    passengers: "乗車人数",
    luggage: "荷物数",
    childSeats: "チャイルドシート",
    childSeatNote: "1台目は無料です。2台目以降は1台につき2,000円です。",
    infantSeats: "ベビーシート",
    infantSeatNote: "1台目から1台につき2,000円です。",
    tripTitleCharter: "貸切ルートを入力",
    tripTitleTransfer: "行程詳細を入力",
    tripSubtitle: "日付、ルート、重要なサービス内容に絞って入力します。",
    contactTitle: "ご利用者情報",
    contactSubtitle: "予約確認と決済リンク送付に必要な連絡先のみ入力してください。",
    reviewTitle: "予約内容を確認する",
    reviewSubtitle: "日時は日本時間で表示されます。内容をご確認のうえ、各ポリシーへ同意してください。",
    serviceTypes: {
      arrival: {
        title: "空港お迎え",
        description: "空港到着後、ドライバーがお迎えして目的地へお送りします。",
        label: "空港お迎え",
      },
      departure: {
        title: "空港お送り",
        description: "空港へ向かう送迎です。フライトに合わせた出発時間を提案します。",
        label: "空港お送り",
      },
      point_to_point: {
        title: "片道送迎",
        description: "ホテル、駅、港、レストラン、都市間などのプライベート送迎です。",
        label: "片道送迎",
      },
      fallback: "空港 / 片道送迎",
    },
    vehicleMeta: {
      alphard: "上質な乗り心地",
      hiace: "グループに最適",
      premium_hiace: "電動シート付き　贅沢な体験",
    },
    vehicleCapacity: {
      alphard: "1〜4名様",
      hiace: "1〜9名様",
      premium_hiace: "1〜9名様",
    },
    vehicleQuote: {
      checking: "確認中",
      pending: "確認見積",
      waiting: "行程を入力",
    },
    fields: {
      date: "ご利用日",
      time: "出発時間",
      pickupTime: "出発時間",
      charterDepartureArea: "貸切出発エリア",
      charterCityArea: "貸切都市 / エリア",
      pickupLocation: "乗車場所",
      dropoffLocation: "降車場所",
      specialLuggage: "特殊なお荷物（任意）",
      charterDetails: "貸切詳細",
      charterHours: "貸切時間",
      stopovers: "立ち寄り地",
      overtimeNote: "延長料金のご案内",
      crossAreaNote: "エリア外のご案内",
      fullName: "氏名",
      email: "メール",
      phone: "電話番号",
      remarks: "備考",
    },
    placeholders: {
      location: "ホテル、駅、港、レストラン、Google Mapsリンクなど",
      specialLuggage: "スキー板、ゴルフバッグ、ベビーカーなど",
      stopovers: "複数の立ち寄り地はカンマで区切ってください",
    },
    notes: {
      overtime: "延長料金：Alphard ¥5,000/時、HiAce ¥7,000/時、Premium HiAce ¥10,000/時。東京以外で開始または終了する場合、見積ルールに基づき利用可能時間が調整されます。",
      crossRegion: "目的地エリアは、この行程で最も遠い目的地を選択してください。",
      timeAfterFlight: "フライト時間と推奨時間を確認してから選択してください。",
      timeFormat: "24時間表記",
      jst: "日本時間",
    },
    flight: {
      panelEyebrow: "フライト認識",
      importDestination: "目的空港を読み込み",
      importArrival: "空港お迎え情報を読み込み",
      detected: "検出",
      airportPending: "空港未確定",
      flightNumber: "便名",
      example: "例：NH 879、JL 62、EK 319",
      importing: "読み込み中...",
      importButton: "フライト情報を読み込む",
      departureTime: "フライト出発時刻",
      landingTime: "フライト到着時刻",
      destinationAirport: "目的空港",
      pickupAirport: "お迎え空港",
      empty: "便名を入力し、「フライト情報を読み込む」を押すと、時刻と空港情報を取得します。",
      checking: "フライト情報を確認中...",
      found: "フライトが見つかりました",
      verified: "フライトデータで確認済み",
      needsManualCheck: "手動確認が必要です",
      autoFilled: "自動入力",
      airport: "空港",
      flightDate: "フライト日",
      pickupTime: "お迎え時間",
      from: "出発地",
      to: "到着地",
      date: "フライト日",
      departure: "出発時刻",
      arrival: "到着時刻",
      confirm: "こちらのフライトで正しいかご確認ください。",
      notConnected: "フライト情報を自動確認できませんでした。",
      manualTime: "フライト時刻を手入力してください。",
      notFound: "フライト情報が見つかりませんでした。",
      checkManual: "便名を確認し、時刻を手入力してください。",
      time: "フライト時刻",
      timePending: "便名未入力",
      flightTimePending: "フライト時刻未入力",
      manualConfirm: "手動で確認してください",
    },
    datePicker: {
      selectTripDate: "利用日を選択",
      previousMonth: "前の月",
      nextMonth: "次の月",
      selectDate: "日付を選択",
      selected: "選択中",
      confirm: "確定",
    },
    nav: {
      back: "戻る",
      continue: "次へ",
      preparing: "準備中...",
      reviewBooking: "予約内容を確認する",
      whatsapp: "WhatsAppで問い合わせる",
    },
    otherArea: {
      title: "このエリアは個別確認が必要です",
      body: "その他の乗車地または降車地は、WhatsAppでお問い合わせください。空き状況と料金を直接確認します。",
      button: "WhatsAppを開く",
    },
    review: {
      cancellationTitle: "キャンセル / 返金規定",
      cancellationBody: "サービス開始48時間以内のキャンセルは返金不可です。未払いの決済リンクは24時間で失効します。",
      privacy: "プライバシーポリシーに同意します",
      terms: "利用規約に同意します",
      cancellation: "キャンセルポリシーに同意します",
      submitting: "送信中...",
      submit: "予約リクエストを送信する",
      rows: {
        serviceType: "サービス種別",
        dateTime: "ご利用日 / 出発時間",
        route: "ルート",
        flight: "フライト",
        vehicle: "車両",
        passengersLuggage: "人数 / 荷物",
        childSeats: "チャイルドシート",
        finalAmount: "最終金額",
      },
      childSeatsValue: "選択 · 1台目無料、2台目以降は1台2,000円",
      pendingQuote: "見積確認中",
    },
    summary: {
      eyebrow: "予約サマリー",
      title: "ご利用内容",
      service: "サービス",
      vehicle: "車両",
      guests: "人数",
      childSeats: "チャイルドシート",
      tripTime: "利用時間",
      route: "ルート",
      flight: "フライト",
      passenger: "乗客",
      chooseVehicle: "車両を選択",
      chooseDateTime: "日付と時間を選択",
      enterPassenger: "連絡先を入力",
      estimatedTotal: "概算合計",
      priceReview: "車両選択画面で料金が表示されます。",
      pricePending: "料金確認中です。",
      urgent: "48時間以内のご予約は、スタッフによる確認が必要です。",
      passengerUnit: "名",
      luggageUnit: "個の荷物",
      childSeatValue: "選択 · 1台目無料、2台目以降2,000円",
    },
    result: {
      received: "予約リクエストを送信しました。",
      title: "確認コードを保存してください",
      format: "形式：SIC-7KQ9P",
      body: "このコードを保存してください。確認コードと氏名で予約内容を確認できます。",
      within48: " 48時間以内のご予約は、スタッフによる確認が必要です。確認後に決済リンクをお送りします。",
      paymentPending: "決済リンク準備中",
      paymentPendingBody: "安全なSquare決済リンクをまもなくお送りします。",
      openPayment: "お支払いへ進む",
      whatsappPayment: "WhatsAppで決済リンクを送る",
      checkBooking: "予約を確認",
    },
    route: {
      airport: "空港",
      addPickup: "乗車場所を入力",
      addDropoff: "降車場所を入力",
      addBoth: "乗車地と降車地を入力",
    },
    recommendation: {
      haneda: "推奨お迎え時間：フライト出発の3時間前。",
      itami: "推奨お迎え時間：フライト出発の3時間前。",
      kansai: "推奨お迎え時間：フライト出発の3.5時間前。",
      narita: "推奨お迎え時間：フライト出発の3.5時間前。",
    },
    quoteFallback: "料金はスタッフが確認します。このままリクエスト内容を確認できます。",
    submitError: "予約を送信できませんでした。",
    validation: {
      service: "サービスを選択してください。",
      tripDateTime: "ご利用日と出発時間を選択してください。",
      route: "乗車場所と降車場所を入力してください。",
      contact: "お名前、メールアドレス、電話番号を入力してください。",
      member: "会員予約を続けるにはログインまたは登録してください。",
    },
  },
  zh: {
    metaTitle: "湘南国际｜机场接送与包车预约",
    eyebrow: "日本私人接送服务",
    title: "预约机场接送或包车服务",
    subtitle: "请选择服务开始预约，我们会引导您完成每一步。",
    steps: ["服务", "行程", "车型", "联系", "确认"],
    serviceStep: "第 1 步 / 共 5 步",
    chooseService: "选择服务",
    chooseServiceSubtitle: "请选择包车或接送开始预约。",
    charterTitle: "包车",
    charterDescription: <>按小时包车<br />专属司机<br />为您服务</>,
    charterMeta: "适合灵活行程",
    transferTitle: "接送",
    transferDescription: <>点对点接送<br />机场或市区<br />专车接送</>,
    transferMeta: "适合直接出行",
    selected: "已选择",
    choose: "选择",
    vehicleTitle: "选择车型",
    vehicleSubtitle: "清楚显示适合人数，方便您安心选择。",
    passengers: "乘客人数",
    luggage: "行李数量",
    childSeats: "儿童座椅",
    childSeatNote: "第一个免费，第二个开始每个 ¥2,000。",
    infantSeats: "婴儿座椅",
    infantSeatNote: "从第一个起，每个 ¥2,000。",
    tripTitleCharter: "填写包车路线",
    tripTitleTransfer: "填写行程信息",
    tripSubtitle: "这里只填写日期、路线和重要服务信息。",
    contactTitle: "乘车人信息",
    contactSubtitle: "仅需要用于确认预约和发送付款链接的联系方式。",
    reviewTitle: "确认预约内容",
    reviewSubtitle: "日期时间均为日本时间。请确认预约内容，并在提交前同意相关政策。",
    serviceTypes: {
      arrival: {
        title: "机场接机",
        description: "乘客抵达机场后，我们接机并送往目的地。",
        label: "机场接机",
      },
      departure: {
        title: "机场送机",
        description: "乘客前往机场，系统会根据航班建议出发时间。",
        label: "机场送机",
      },
      point_to_point: {
        title: "点对点接送",
        description: "酒店、车站、港口、餐厅或城市间专车接送。",
        label: "点对点接送",
      },
      fallback: "机场 / 点对点接送",
    },
    vehicleMeta: {
      alphard: "豪华舒适",
      hiace: "适合多人同行",
      premium_hiace: "电动座椅 · 豪华体验",
    },
    vehicleCapacity: {
      alphard: "1-4 名乘客",
      hiace: "1-9 名乘客",
      premium_hiace: "1-9 名乘客",
    },
    vehicleQuote: {
      checking: "计算中",
      pending: "人工确认",
      waiting: "填写行程",
    },
    fields: {
      date: "出发日期",
      time: "出发时间",
      pickupTime: "上车时间",
      charterDepartureArea: "包车出发地区",
      charterCityArea: "包车城市 / 地区",
      pickupLocation: "上车地点",
      dropoffLocation: "下车地点",
      specialLuggage: "特殊行李（选填）",
      charterDetails: "包车详情",
      charterHours: "包车小时数",
      stopovers: "停靠点",
      overtimeNote: "超时说明",
      crossAreaNote: "跨区域说明",
      fullName: "姓名",
      email: "邮箱",
      phone: "电话",
      remarks: "备注",
    },
    placeholders: {
      location: "酒店、车站、港口、餐厅、Google Maps 链接等",
      specialLuggage: "滑雪板、高尔夫包、婴儿车等",
      stopovers: "多个停靠点请用逗号分隔",
    },
    notes: {
      overtime: "超时费：Alphard ¥5,000/小时，HiAce ¥7,000/小时，Premium HiAce ¥10,000/小时。异地开始或异地结束时，会按报价规则扣减可用服务时间。",
      crossRegion: "目的地区域请选择本次出行距离最远的目的地。",
      timeAfterFlight: "请在确认航班时间和建议时间后选择。",
      timeFormat: "24小时制",
      jst: "日本时间",
    },
    flight: {
      panelEyebrow: "航班识别",
      importDestination: "导入目的地机场",
      importArrival: "导入接机信息",
      detected: "已识别",
      airportPending: "机场待确认",
      flightNumber: "航班号",
      example: "例如：NH 879、JL 62、EK 319",
      importing: "导入中...",
      importButton: "导入航班信息",
      departureTime: "航班起飞时间",
      landingTime: "航班落地时间",
      destinationAirport: "目的地机场",
      pickupAirport: "接机机场",
      empty: "输入航班号后，点击导入航班信息即可读取航班时间和机场。",
      checking: "正在查询航班信息...",
      found: "已找到航班",
      verified: "已通过航班数据验证",
      needsManualCheck: "需要手动确认",
      autoFilled: "已自动填写",
      airport: "机场",
      flightDate: "航班日期",
      pickupTime: "上车时间",
      from: "出发地",
      to: "到达地",
      date: "航班日期",
      departure: "起飞时间",
      arrival: "落地时间",
      confirm: "请确认这是否为您的正确航班。",
      notConnected: "我们无法自动验证该航班。",
      manualTime: "请手动填写航班时间。",
      notFound: "未找到航班信息。",
      checkManual: "请检查航班号，并手动确认时间。",
      time: "航班时间",
      timePending: "航班号待填写",
      flightTimePending: "航班时间待填写",
      manualConfirm: "请手动确认",
    },
    datePicker: {
      selectTripDate: "选择用车日期",
      previousMonth: "上个月",
      nextMonth: "下个月",
      selectDate: "选择日期",
      selected: "已选择",
      confirm: "确定",
    },
    nav: {
      back: "返回",
      continue: "继续",
      preparing: "准备中...",
      reviewBooking: "确认预约内容",
      whatsapp: "通过 WhatsApp 联系我们",
    },
    otherArea: {
      title: "该地区需要人工确认",
      body: "如果上车或下车地区选择其他，请通过 WhatsApp 联系我们。我们会直接确认车辆和价格。",
      button: "打开 WhatsApp",
    },
    review: {
      cancellationTitle: "取消 / 退款规则",
      cancellationBody: "服务开始前48小时内取消不可退款。未付款链接将在24小时后失效。",
      privacy: "我同意隐私政策",
      terms: "我同意利用规约",
      cancellation: "我同意取消政策",
      submitting: "提交中...",
      submit: "提交预约请求",
      rows: {
        serviceType: "服务类型",
        dateTime: "出发日期 / 出发时间",
        route: "路线",
        flight: "航班",
        vehicle: "车型",
        passengersLuggage: "人数 / 行李",
        childSeats: "儿童座椅",
        finalAmount: "最终金额",
      },
      childSeatsValue: "已选择 · 第一个免费，第二个开始每个 ¥2,000",
      pendingQuote: "等待报价",
    },
    summary: {
      eyebrow: "预约摘要",
      title: "您的行程",
      service: "服务",
      vehicle: "车型",
      guests: "乘客",
      childSeats: "儿童座椅",
      tripTime: "用车时间",
      route: "路线",
      flight: "航班",
      passenger: "乘车人",
      chooseVehicle: "选择车型",
      chooseDateTime: "选择日期和时间",
      enterPassenger: "填写乘车人联系方式",
      estimatedTotal: "预计总额",
      priceReview: "价格会在车型步骤显示。",
      pricePending: "价格待确认。",
      urgent: "48小时以内的预约需由工作人员确认后生效。",
      passengerUnit: "名乘客",
      luggageUnit: "件行李",
      childSeatValue: "已选择 · 第一个免费，之后每个 ¥2,000",
    },
    result: {
      received: "预约请求已提交",
      title: "请保存确认码",
      format: "格式：SIC-7KQ9P",
      body: "请保存此确认码。您可以用确认码和姓名查询预约信息。",
      within48: " 本次预约在48小时内，需由工作人员确认后再发送付款链接。",
      paymentPending: "付款链接准备中",
      paymentPendingBody: "我们会尽快发送安全的 Square 付款链接。",
      openPayment: "继续付款",
      whatsappPayment: "通过 WhatsApp 发送付款链接",
      checkBooking: "查询预约",
    },
    route: {
      airport: "机场",
      addPickup: "填写上车地点",
      addDropoff: "填写下车地点",
      addBoth: "填写上车和下车地点",
    },
    recommendation: {
      haneda: "建议上车时间：航班起飞前3小时。",
      itami: "建议上车时间：航班起飞前3小时。",
      kansai: "建议上车时间：航班起飞前3.5小时。",
      narita: "建议上车时间：航班起飞前3.5小时。",
    },
    quoteFallback: "价格将由工作人员确认。您仍然可以继续确认预约内容。",
    submitError: "无法提交预约请求。",
    validation: {
      service: "请先选择服务。",
      tripDateTime: "请选择出发日期和出发时间。",
      route: "请填写上车地点和下车地点。",
      contact: "请填写姓名、邮箱和电话。",
      member: "请先登录或注册后再继续会员下单。",
    },
  },
  zhHant: {
    metaTitle: "湘南國際｜機場接送與包車預約",
    eyebrow: "日本私人接送服務",
    title: "預約機場接送或包車服務",
    subtitle: "請選擇服務開始預約，我們會引導您完成每一步。",
    steps: ["服務", "行程", "車型", "聯絡", "確認"],
    serviceStep: "第 1 步 / 共 5 步",
    chooseService: "選擇服務",
    chooseServiceSubtitle: "請選擇包車或接送開始預約。",
    charterTitle: "包車",
    charterDescription: <>按小時包車<br />專屬司機<br />為您服務</>,
    charterMeta: "適合彈性行程",
    transferTitle: "接送",
    transferDescription: <>點對點接送<br />機場或市區<br />專車接送</>,
    transferMeta: "適合直接出行",
    selected: "已選擇",
    choose: "選擇",
    vehicleTitle: "選擇車型",
    vehicleSubtitle: "清楚顯示適合人數，方便您安心選擇。",
    passengers: "乘客人數",
    luggage: "行李數量",
    childSeats: "兒童座椅",
    childSeatNote: "第一個免費，第二個開始每個 ¥2,000。",
    infantSeats: "嬰兒座椅",
    infantSeatNote: "從第一個起，每個 ¥2,000。",
    tripTitleCharter: "填寫包車路線",
    tripTitleTransfer: "填寫行程資訊",
    tripSubtitle: "這裡只填寫日期、路線和重要服務資訊。",
    contactTitle: "乘車人資訊",
    contactSubtitle: "僅需要用於確認預約和發送付款連結的聯絡方式。",
    reviewTitle: "確認預約內容",
    reviewSubtitle: "日期時間均為日本時間。請確認預約內容，並在提交前同意相關政策。",
    serviceTypes: {
      arrival: { title: "機場接機", description: "乘客抵達機場後，我們接機並送往目的地。", label: "機場接機" },
      departure: { title: "機場送機", description: "乘客前往機場，系統會根據航班建議出發時間。", label: "機場送機" },
      point_to_point: { title: "點對點接送", description: "酒店、車站、港口、餐廳或城市間專車接送。", label: "點對點接送" },
      fallback: "機場 / 點對點接送",
    },
    vehicleMeta: { alphard: "豪華舒適", hiace: "適合多人同行", premium_hiace: "電動座椅 · 豪華體驗" },
    vehicleCapacity: { alphard: "1-4 名乘客", hiace: "1-9 名乘客", premium_hiace: "1-9 名乘客" },
    vehicleQuote: { checking: "計算中", pending: "人工確認", waiting: "填寫行程" },
    fields: {
      date: "出發日期", time: "出發時間", pickupTime: "上車時間", charterDepartureArea: "包車出發地區", charterCityArea: "包車城市 / 地區",
      pickupLocation: "上車地點", dropoffLocation: "下車地點", specialLuggage: "特殊行李（選填）", charterDetails: "包車詳情",
      charterHours: "包車小時數", stopovers: "停靠點", overtimeNote: "超時說明", crossAreaNote: "跨區域說明",
      fullName: "姓名", email: "電郵", phone: "電話", remarks: "備註",
    },
    placeholders: {
      location: "酒店、車站、港口、餐廳、Google Maps 連結等",
      specialLuggage: "滑雪板、高爾夫球包、嬰兒車等",
      stopovers: "多個停靠點請用逗號分隔",
    },
    notes: {
      overtime: "超時費：Alphard ¥5,000/小時，HiAce ¥7,000/小時，Premium HiAce ¥10,000/小時。異地開始或異地結束時，會按報價規則扣減可用服務時間。",
      crossRegion: "目的地區域請選擇本次出行距離最遠的目的地。",
      timeAfterFlight: "請在確認航班時間和建議時間後選擇。",
      timeFormat: "24小時制",
      jst: "日本時間",
    },
    flight: {
      panelEyebrow: "航班識別", importDestination: "導入目的地機場", importArrival: "導入接機資訊", detected: "已識別",
      airportPending: "機場待確認", flightNumber: "航班號", example: "例如：NH 879、JL 62、EK 319", importing: "導入中...",
      importButton: "導入航班資訊", departureTime: "航班起飛時間", landingTime: "航班落地時間", destinationAirport: "目的地機場",
      pickupAirport: "接機機場", empty: "輸入航班號後，點擊導入航班資訊即可讀取航班時間和機場。",
      checking: "正在查詢航班資訊...", found: "已找到航班", verified: "已通過航班資料驗證", needsManualCheck: "需要手動確認", autoFilled: "已自動填寫", airport: "機場", flightDate: "航班日期",
      pickupTime: "上車時間", from: "出發地", to: "到達地", date: "航班日期", departure: "起飛時間", arrival: "落地時間",
      confirm: "請確認這是否為您的正確航班。", notConnected: "我們無法自動驗證該航班。", manualTime: "請手動填寫航班時間。",
      notFound: "未找到航班資訊。", checkManual: "請檢查航班號，並手動確認時間。", time: "航班時間",
      timePending: "航班號待填寫", flightTimePending: "航班時間待填寫", manualConfirm: "請手動確認",
    },
    datePicker: { selectTripDate: "選擇用車日期", previousMonth: "上個月", nextMonth: "下個月", selectDate: "選擇日期", selected: "已選擇", confirm: "確定" },
    nav: { back: "返回", continue: "繼續", preparing: "準備中...", reviewBooking: "確認預約內容", whatsapp: "透過 WhatsApp 聯絡我們" },
    otherArea: {
      title: "該地區需要人工確認",
      body: "如果上車或下車地區選擇其他，請透過 WhatsApp 聯絡我們。我們會直接確認車輛和價格。",
      button: "打開 WhatsApp",
    },
    review: {
      cancellationTitle: "取消 / 退款規則",
      cancellationBody: "服務開始前48小時內取消不可退款。未付款連結將在24小時後失效。",
      privacy: "我同意隱私政策", terms: "我同意利用規約", cancellation: "我同意取消政策", submitting: "提交中...", submit: "提交預約請求",
      rows: { serviceType: "服務類型", dateTime: "出發日期 / 出發時間", route: "路線", flight: "航班", vehicle: "車型", passengersLuggage: "人數 / 行李", childSeats: "兒童座椅", finalAmount: "最終金額" },
      childSeatsValue: "已選擇 · 第一個免費，第二個開始每個 ¥2,000", pendingQuote: "等待報價",
    },
    summary: {
      eyebrow: "預約摘要", title: "您的行程", service: "服務", vehicle: "車型", guests: "乘客", childSeats: "兒童座椅",
      tripTime: "用車時間", route: "路線", flight: "航班", passenger: "乘車人", chooseVehicle: "選擇車型",
      chooseDateTime: "選擇日期和時間", enterPassenger: "填寫乘車人聯絡方式", estimatedTotal: "預計總額",
      priceReview: "價格會在車型步驟顯示。", pricePending: "價格待確認。", urgent: "48小時以內的預約需由工作人員確認後生效。",
      passengerUnit: "名乘客", luggageUnit: "件行李", childSeatValue: "已選擇 · 第一個免費，之後每個 ¥2,000",
    },
    result: {
      received: "預約請求已提交", title: "請保存確認碼", format: "格式：SIC-7KQ9P",
      body: "請保存此確認碼。您可以用確認碼和姓名查詢預約資訊。",
      within48: " 本次預約在48小時內，需由工作人員確認後再發送付款連結。",
      paymentPending: "付款連結準備中", paymentPendingBody: "我們會盡快發送安全的 Square 付款連結。",
      openPayment: "繼續付款", whatsappPayment: "透過 WhatsApp 發送付款連結", checkBooking: "查詢預約",
    },
    route: { airport: "機場", addPickup: "填寫上車地點", addDropoff: "填寫下車地點", addBoth: "填寫上車和下車地點" },
    recommendation: { haneda: "建議上車時間：航班起飛前3小時。", itami: "建議上車時間：航班起飛前3小時。", kansai: "建議上車時間：航班起飛前3.5小時。", narita: "建議上車時間：航班起飛前3.5小時。" },
    quoteFallback: "價格將由工作人員確認。您仍然可以繼續確認預約內容。",
    submitError: "無法提交預約請求。",
    validation: {
      service: "請先選擇服務。",
      tripDateTime: "請選擇出發日期和出發時間。",
      route: "請填寫上車地點和下車地點。",
      contact: "請填寫姓名、電郵和電話。",
      member: "請先登入或註冊後再繼續會員下單。",
    },
  },
};

const initialForm = {
  service_type: "transfer",
  transfer_subtype: "arrival",
  full_name: "",
  email: "",
  phone: "",
  phone_country_code: "JP",
  departure_date: "",
  pickup_time: "",
  vehicle_type: "alphard",
  passenger_count: 1,
  luggage_count: 0,
  pickup_area: "tokyo",
  dropoff_area: "tokyo",
  pickup_location: "",
  destination: "",
  pickup_postal_code: "",
  pickup_prefecture: "",
  pickup_city: "",
  pickup_street: "",
  pickup_building: "",
  pickup_maps_url: "",
  dropoff_postal_code: "",
  dropoff_prefecture: "",
  dropoff_city: "",
  dropoff_street: "",
  dropoff_building: "",
  dropoff_maps_url: "",
  remarks: "",
  flight_direction: "arrival",
  flight_service_date: "",
  flight_service_time: "",
  flight_number: "",
  signboard_name: "",
  child_seat_count: 0,
  infant_seat_0_3_count: 0,
  toll_parking_note: "Toll and parking inclusion depends on the route and final confirmation.",
  pickup_address_detail: "",
  dropoff_address_detail: "",
  hotel_name_or_maps_url: "",
  special_luggage: "",
  selected_hours: 8,
  charter_area: "tokyo",
  stopovers: "",
  end_location: "tokyo",
  end_location_in_base: true,
  end_location_other: "",
  cross_region_note: "The itinerary must be confirmed. Additional charges may apply outside the quoted area.",
  accepted_privacy_policy: false,
  accepted_terms: false,
  accepted_cancellation_policy: false,
  retail_member_token: "",
  retail_credit_to_apply_jpy: 0,
  retail_points_to_apply: 0,
};

const transferSubtypes = [
  {
    key: "arrival",
    title: "Airport pickup",
    description: "Passenger arrives at the airport. We meet and transfer to the destination.",
  },
  {
    key: "departure",
    title: "Airport drop-off",
    description: "Passenger goes to the airport. We suggest pickup timing for the flight.",
  },
  {
    key: "point_to_point",
    title: "Point-to-point",
    description: "Hotel, station, port, restaurant, or city-to-city private transfer.",
  },
];

const FALLBACK_AREAS = [
  { code: "tokyo", name_en: "Tokyo", name_zh: "东京", name_zhHant: "東京", name_ja: "東京", aliases: ["東京駅", "东京站", "銀座", "银座", "ginza", "新宿", "shinjuku", "渋谷", "涩谷", "shibuya", "上野", "ueno", "浅草", "asakusa", "品川", "shinagawa", "六本木", "roppongi", "お台場", "台场", "odaiba", "丸の内", "marunouchi", "赤坂", "akasaka", "秋葉原", "akihabara"] },
  { code: "narita_airport", name_en: "Narita Airport", name_zh: "成田机场", name_zhHant: "成田機場", name_ja: "成田空港", aliases: ["nrt", "narita", "成田国際空港", "成田国际机场", "成田國際機場"] },
  { code: "haneda_airport", name_en: "Haneda Airport", name_zh: "羽田机场", name_zhHant: "羽田機場", name_ja: "羽田空港", aliases: ["hnd", "haneda", "東京国際空港", "东京国际机场", "東京國際機場"] },
  { code: "kansai_airport", name_en: "Kansai Airport", name_zh: "关西机场", name_zhHant: "關西機場", name_ja: "関西空港", aliases: ["kix", "kansai airport", "kansai international airport", "関西国際空港", "関空", "关西机场", "關西機場"] },
  { code: "itami_airport", name_en: "Itami Airport", name_zh: "伊丹机场", name_zhHant: "伊丹機場", name_ja: "伊丹空港", aliases: ["itm", "itami", "osaka international airport", "大阪国際空港", "伊丹机场", "伊丹機場"] },
  { code: "hakone", name_en: "Hakone", name_zh: "箱根", name_zhHant: "箱根", name_ja: "箱根", aliases: ["箱根湯本", "箱根汤本", "強羅", "gora"] },
  { code: "mt_fuji", name_en: "Mt. Fuji", name_zh: "富士山", name_zhHant: "富士山", name_ja: "富士山", aliases: ["fuji", "mt fuji", "mount fuji", "河口湖", "kawaguchiko", "富士五湖", "富士山五合目"] },
  { code: "nikko", name_en: "Nikko", name_zh: "日光", name_zhHant: "日光", name_ja: "日光", aliases: ["東照宮", "东照宫", "toshogu"] },
  { code: "kawagoe", name_en: "Kawagoe", name_zh: "川越", name_zhHant: "川越", name_ja: "川越", aliases: ["Kawagoe", "川越"] },
  { code: "yokohama", name_en: "Yokohama", name_zh: "横滨", name_zhHant: "橫濱", name_ja: "横浜", aliases: ["yokohama", "みなとみらい", "minatomirai", "横浜港", "大さん橋"] },
  { code: "kamakura", name_en: "Kamakura", name_zh: "镰仓", name_zhHant: "鎌倉", name_ja: "鎌倉", aliases: ["kamakura", "江ノ島", "江之岛", "enoshima"] },
  { code: "shizuoka", name_en: "Shizuoka", name_zh: "静冈", name_zhHant: "靜岡", name_ja: "静岡", aliases: ["shizuoka", "清水", "shimizu"] },
  { code: "karuizawa", name_en: "Karuizawa", name_zh: "轻井泽", name_zhHant: "輕井澤", name_ja: "軽井沢", aliases: ["karuizawa"] },
  { code: "izu", name_en: "Izu", name_zh: "伊豆", name_zhHant: "伊豆", name_ja: "伊豆", aliases: ["伊東", "ito", "熱海", "atami", "下田", "shimoda"] },
  { code: "osaka", name_en: "Osaka", name_zh: "大阪", name_zhHant: "大阪", name_ja: "大阪", aliases: ["osaka", "osaka city", "大阪市"] },
  { code: "kyoto", name_en: "Kyoto", name_zh: "京都", name_zhHant: "京都", name_ja: "京都", aliases: ["kyoto"] },
  { code: "kobe", name_en: "Kobe", name_zh: "神户", name_zhHant: "神戶", name_ja: "神戸", aliases: ["kobe"] },
  { code: "nara", name_en: "Nara", name_zh: "奈良", name_zhHant: "奈良", name_ja: "奈良", aliases: ["nara"] },
  { code: "kansai", name_en: "Kansai", name_zh: "关西", name_zhHant: "關西", name_ja: "関西", aliases: ["osaka", "大阪", "kyoto", "京都", "kobe", "神戸", "神户"] },
  { code: "hokkaido", name_en: "Hokkaido", name_zh: "北海道", name_zhHant: "北海道", name_ja: "北海道", aliases: ["sapporo", "札幌", "新千歳", "新千岁", "chitose"] },
];

const staticAreaAliases = FALLBACK_AREAS.map((area) => ({
  code: area.code,
  keywords: [
    area.name_en,
    area.name_zh,
    area.name_zhHant,
    area.name_ja,
    ...(area.aliases || []),
  ],
}));

const steps = [
  { key: "service", label: "Service" },
  { key: "trip", label: "Trip" },
  { key: "vehicle", label: "Vehicle" },
  { key: "contact", label: "Contact" },
  { key: "review", label: "Review" },
];

const cardVisuals = {
  transfer: { image: publicBookingLuxuryFleet, position: "center 64%" },
  charter: { image: publicBookingChauffeurCharter, position: "center 64%" },
  arrival: { image: tripSubtypeArrivalImage, position: "center 24%" },
  departure: { image: tripSubtypeDepartureImage, position: "center 24%" },
  point_to_point: { image: tripSubtypePointToPointImage, position: "center 26%" },
  alphard: { image: vehicleAlphardImage, position: "center 48%" },
  hiace: { image: vehicleHiaceImage, position: "center 50%" },
  premium_hiace: { image: vehiclePremiumHiaceImage, position: "center 45%" },
};

const memberGateCopy = {
  en: {
    eyebrow: "Member Booking",
    title: "Sign in to continue",
    body: "Log in to earn 3% to 5% points based on your member level, and use your credit or points toward future private transfers.",
    guestLink: "Continue as guest",
    benefits: [
      "3% to 5% points after each completed member booking",
      "Use credit and points on your next ride",
      "Manage rewards with a dedicated guest account",
    ],
  },
  ja: {
    eyebrow: "会員予約",
    title: "ログインして続行",
    body: "ログインして予約すると、会員ランクに応じて3%から5%のポイントが貯まり、残高やポイントを次回の送迎に利用できます。",
    guestLink: "ゲスト予約で続ける",
    benefits: [
      "会員予約の完了後、会員ランクに応じて3%から5%のポイントを付与",
      "次回の送迎で残高とポイントを利用可能",
      "お客様専用アカウントで特典を管理",
    ],
  },
  zh: {
    eyebrow: "会员下单",
    title: "请先登录或注册",
    body: "登录后下单可按会员等级获得3%至5%积分，并可使用账户余额或积分抵扣之后的私人接送订单。",
    guestLink: "继续游客下单",
    benefits: [
      "会员订单完成后返还3%至5%积分",
      "余额和积分可用于下次接送抵扣",
      "使用独立客人账户管理会员权益",
    ],
  },
  zhHant: {
    eyebrow: "會員下單",
    title: "請先登入或註冊",
    body: "登入後下單可按會員等級獲得3%至5%積分，並可使用帳戶餘額或積分折抵之後的私人接送訂單。",
    guestLink: "繼續旅客下單",
    benefits: [
      "會員訂單完成後返還3%至5%積分",
      "餘額和積分可用於下次接送折抵",
      "使用獨立客人帳戶管理會員權益",
    ],
  },
};

const shellActionCopy = {
  en: { login: "Login", member: "Member" },
  ja: { login: "ログイン", member: "会員" },
  zh: { login: "登录", member: "会员" },
  zhHant: { login: "登入", member: "會員" },
};

const reviewAuthChoiceCopy = {
  en: {
    title: "How would you like to submit?",
    body: "You can submit as a guest now, or sign in before submitting to earn 3% to 5% points after this booking is completed.",
    guestTitle: "Continue as guest",
    guestBody: "No registration required. Submit this booking request now.",
    guestButton: "Use guest booking",
    memberTitle: "Login / Register",
    memberBody: "Earn 3% to 5% points and use account credit or points for future rides.",
    memberButton: "Login / Register before submitting",
  },
  ja: {
    title: "送信方法をお選びください",
    body: "このままゲストとして送信できます。送信前にログインすると、完了後に会員ランクに応じて3%から5%のポイントを獲得できます。",
    guestTitle: "ゲストとして続ける",
    guestBody: "登録不要。この予約リクエストをそのまま送信できます。",
    guestButton: "ゲスト予約で送信",
    memberTitle: "ログイン / 登録",
    memberBody: "3%から5%のポイントを獲得し、次回の送迎で残高やポイントを利用できます。",
    memberButton: "ログイン / 登録して送信",
  },
  zh: {
    title: "请选择提交方式",
    body: "您可以直接以游客身份提交预约，也可以先登录/注册，订单完成后按会员等级获得3%至5%积分。",
    guestTitle: "游客继续提交",
    guestBody: "无需注册，当前预约信息可以直接提交。",
    guestButton: "使用游客下单",
    memberTitle: "登录 / 注册",
    memberBody: "登录后可获得3%至5%积分，并可在下次使用余额或积分抵扣。",
    memberButton: "登录 / 注册后提交",
  },
  zhHant: {
    title: "請選擇提交方式",
    body: "您可以直接以旅客身份提交預約，也可以先登入/註冊，訂單完成後按會員等級獲得3%至5%積分。",
    guestTitle: "旅客繼續提交",
    guestBody: "無需註冊，當前預約資訊可以直接提交。",
    guestButton: "使用旅客下單",
    memberTitle: "登入 / 註冊",
    memberBody: "登入後可獲得3%至5%積分，並可在下次使用餘額或積分折抵。",
    memberButton: "登入 / 註冊後提交",
  },
};

const contactChoiceCopy = {
  en: {
    title: "Contact method",
    body: "Continue as a guest, or sign in to use your saved member information.",
    guestTitle: "Guest",
    guestBody: "No login required. Enter contact details for this booking only.",
    guestButton: "Use guest contact",
    memberTitle: "Member login",
    memberBody: "Sign in to use saved contact details, earn 3% to 5% points, and apply rewards next time.",
    memberButton: "Login / Sign up",
    memberActiveButton: "Using member details",
    recommendedBadge: "Recommended",
    memberInfoTitle: "Member information",
    memberInfoBody: "These saved account details will be used for this booking.",
    notSaved: "Not saved",
    memberLoginRequired: "Please log in or sign up to continue with Member Booking.",
  },
  ja: {
    title: "連絡先の入力方法",
    body: "ゲストとして入力するか、会員ログインして保存済みのお客様情報を利用できます。",
    guestTitle: "ゲスト",
    guestBody: "ログイン不要。この予約だけに使用する連絡先を入力します。",
    guestButton: "ゲスト情報を使用",
    memberTitle: "会員ログイン",
    memberBody: "ログインすると保存済みの連絡先を利用でき、3%から5%のポイントも獲得できます。",
    memberButton: "ログイン / 登録",
    memberActiveButton: "会員情報を使用中",
    recommendedBadge: "おすすめ",
    memberInfoTitle: "会員情報",
    memberInfoBody: "保存済みのアカウント情報をこの予約に使用します。",
    notSaved: "未登録",
    memberLoginRequired: "会員予約を続けるには、ログインまたは登録してください。",
  },
  zh: {
    title: "联系方式",
    body: "可以用访客方式填写，也可以登录会员，自动使用账户资料。",
    guestTitle: "访客",
    guestBody: "无需登录，仅为本次预约填写联系方式。",
    guestButton: "使用访客信息",
    memberTitle: "用户登录",
    memberBody: "登录后自动带出账户信息，并可获得3%至5%积分和后续奖励。",
    memberButton: "登录 / 注册",
    memberActiveButton: "已使用用户信息",
    recommendedBadge: "推荐",
    memberInfoTitle: "用户信息",
    memberInfoBody: "系统将使用以下账户信息提交本次预约。",
    notSaved: "未保存",
    memberLoginRequired: "请先登录或注册后再继续会员下单。",
  },
  zhHant: {
    title: "聯絡方式",
    body: "可以用旅客方式填寫，也可以登入會員，自動使用帳戶資料。",
    guestTitle: "旅客",
    guestBody: "無需登入，僅為本次預約填寫聯絡方式。",
    guestButton: "使用旅客資料",
    memberTitle: "用戶登入",
    memberBody: "登入後自動帶出帳戶資料，並可獲得3%至5%積分和後續獎勵。",
    memberButton: "登入 / 註冊",
    memberActiveButton: "已使用用戶資料",
    recommendedBadge: "推薦",
    memberInfoTitle: "用戶資料",
    memberInfoBody: "系統將使用以下帳戶資料提交本次預約。",
    notSaved: "未保存",
    memberLoginRequired: "請先登入或註冊後再繼續會員下單。",
  },
};

const bookingDraftCopy = {
  en: {
    title: "Continue your saved booking?",
    body: "We found details saved on this device.",
    restore: "Restore",
    dismiss: "Start fresh",
  },
  ja: {
    title: "保存済みの予約を続けますか？",
    body: "この端末に入力途中の内容があります。",
    restore: "復元する",
    dismiss: "新しく始める",
  },
  zh: {
    title: "继续上次未完成的预约？",
    body: "我们在这台设备上找到了已保存的填写内容。",
    restore: "恢复填写",
    dismiss: "重新开始",
  },
  zhHant: {
    title: "繼續上次未完成的預約？",
    body: "我們在這台裝置上找到了已儲存的填寫內容。",
    restore: "恢復填寫",
    dismiss: "重新開始",
  },
};

function getInitialPublicBookingLang() {
  if (typeof window === "undefined") return "en";
  const params = new URLSearchParams(window.location.search);
  const queryLang = params.get("lang");
  if (publicBookingCopy[queryLang]) return queryLang;
  const saved = window.localStorage.getItem("public_booking_lang");
  return publicBookingCopy[saved] ? saved : "en";
}

function labelForLang(item, lang) {
  if (!item) return "";
  if (lang === "ja") return item.name_ja || item.name_en || item.name || "";
  if (lang === "zhHant") return item.name_zhHant || item.name_zh_hant || item.name_zh || item.name_en || item.name || "";
  if (lang === "zh") return item.name_zh || item.name_en || item.name || "";
  return item.name_en || item.name || "";
}

function hasMeaningfulPublicBookingDraft(form) {
  return [
    form?.full_name,
    form?.email,
    form?.phone,
    form?.departure_date,
    form?.pickup_time,
    form?.pickup_location,
    form?.destination,
    form?.flight_number,
    form?.remarks,
  ].some((value) => String(value || "").trim());
}

export default function PublicBookingPage({ initialBookingMode = "quick", lockedBookingMode = false }) {
  const [lang, setLangState] = useState(getInitialPublicBookingLang);
  const [bookingMode, setBookingMode] = useState(initialBookingMode);
  const [retailMember, setRetailMember] = useState(null);
  const [retailMemberToken, setRetailMemberToken] = useState(localStorage.getItem("retail_member_token") || "");
  const [config, setConfig] = useState({ areas: [], vehicles: [], charter_hours: [5, 8, 10] });
  const [entry] = useState(() => publicBookingEntry(window.location.search));
  const [form, setForm] = useState(() => entry.service ? { ...initialForm, service_type: entry.service } : initialForm);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [vehicleQuotes, setVehicleQuotes] = useState({});
  const [vehicleQuotesLoading, setVehicleQuotesLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(entry.step);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [result, setResult] = useState(null);
  const [flightLookup, setFlightLookup] = useState({ status: "idle" });
  const [duplicateRisk, setDuplicateRisk] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [authPanelOpen, setAuthPanelOpen] = useState(false);
  const [draftNotice, setDraftNotice] = useState(null);
  const mapResolutionCacheRef = useRef({});
  const wizardTopRef = useRef(null);
  const activeStepScrollReadyRef = useRef(false);
  const copy = publicBookingCopy[lang] || publicBookingCopy.en;

  const setLang = (value) => {
    setLangState(value);
    window.localStorage.setItem("public_booking_entry_seen", "1");
    window.localStorage.setItem("public_booking_lang", value);
    if (value === "ja") {
      window.location.assign("/book/ja");
    }
  };

  const applyRetailMemberContactToForm = useCallback((member, token = retailMemberToken) => {
    if (!member) return;
    setForm((prev) => ({
      ...prev,
      retail_member_token: token || prev.retail_member_token,
      full_name: String(member.full_name || "").trim() || prev.full_name,
      email: String(member.email || "").trim() || prev.email,
      phone: String(member.phone || "").trim() || prev.phone,
    }));
  }, [retailMemberToken]);

  useEffect(() => {
    axios.get(`${API}/public-booking/config`).then((response) => setConfig(response.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = JSON.parse(window.localStorage.getItem(PUBLIC_BOOKING_DRAFT_KEY) || "null");
      if (saved?.form && hasMeaningfulPublicBookingDraft(saved.form)) {
        setDraftNotice(saved);
      }
    } catch (error) {
      window.localStorage.removeItem(PUBLIC_BOOKING_DRAFT_KEY);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || result) return;
    const timer = window.setTimeout(() => {
      if (!hasMeaningfulPublicBookingDraft(form)) return;
      window.localStorage.setItem(PUBLIC_BOOKING_DRAFT_KEY, JSON.stringify({
        form,
        bookingMode,
        activeStep,
        savedAt: new Date().toISOString(),
      }));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [form, bookingMode, activeStep, result]);

  const restoreBookingDraft = () => {
    if (!draftNotice?.form) return;
    setForm((current) => ({ ...current, ...draftNotice.form }));
    if (draftNotice.bookingMode) setBookingMode(draftNotice.bookingMode);
    if (Number.isInteger(draftNotice.activeStep)) {
      setActiveStep(Math.max(0, Math.min(draftNotice.activeStep, steps.length - 1)));
    }
    setDraftNotice(null);
  };

  const dismissBookingDraft = () => {
    window.localStorage.removeItem(PUBLIC_BOOKING_DRAFT_KEY);
    setDraftNotice(null);
  };

  useEffect(() => {
    if (!retailMemberToken) return;
    update("retail_member_token", retailMemberToken);
    axios.get(`${API}/public-booking/member/me`, { headers: { "X-Retail-Member-Token": retailMemberToken } })
      .then((response) => {
        setRetailMember(response.data.member);
        setBookingMode("member");
        applyRetailMemberContactToForm(response.data.member, retailMemberToken);
      })
      .catch(() => {
        localStorage.removeItem("retail_member_token");
        setRetailMember(null);
        setRetailMemberToken("");
        updateMany({
          retail_member_token: "",
          retail_credit_to_apply_jpy: 0,
          retail_points_to_apply: 0,
        });
      });
  }, [retailMemberToken, initialBookingMode, lockedBookingMode, applyRetailMemberContactToForm]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = copy.metaTitle || copy.title;
    return () => {
      document.title = previousTitle;
    };
  }, [copy.metaTitle, copy.title]);

  const areas = useMemo(() => (config.areas?.length ? config.areas : FALLBACK_AREAS), [config.areas]);
  const areaOptions = useMemo(() => ([
    ...areas.filter((area) => area.code !== "other"),
    { code: "other", name_en: "Other", name_zh: "其他地区", name_zhHant: "其他地區", name_ja: "その他" },
  ]), [areas]);
  const areaRecognitionRules = useMemo(() => buildAreaRecognitionRules(areaOptions), [areaOptions]);

  const vehicles = useMemo(() => (config.vehicles?.length ? config.vehicles : [
    { code: "alphard", name: "Toyota Alphard", capacity: "1-4 passengers" },
    { code: "hiace", name: "Toyota Hiace", capacity: "1-9 passengers" },
    { code: "premium_hiace", name: "Premium Hiace", capacity: "1-9 passengers" },
  ]), [config.vehicles]);

  const currentStep = steps[activeStep]?.key || "service";
  const hasOtherArea = form.pickup_area === "other" || form.dropoff_area === "other";
  const isTransfer = form.service_type === "transfer";
  const isArrival = isTransfer && form.transfer_subtype === "arrival";
  const isDeparture = isTransfer && form.transfer_subtype === "departure";
  const isPointToPoint = isTransfer && form.transfer_subtype === "point_to_point";
  const isKansaiArea = (areaCode) => {
    const area = areas.find((item) => item.code === areaCode);
    if (!area) return false;
    const allowedBases = area.allowed_bases || area.available_bases || [];
    const ruleGroup = area.rule_group || "";
    return allowedBases.includes("osaka") || ruleGroup.includes("kansai") || ruleGroup.includes("osaka");
  };
  const isKansaiPremiumHiaceCharter = (
    form.service_type === "charter" &&
    form.vehicle_type === "premium_hiace" &&
    isKansaiArea(form.pickup_area)
  );
  const charterHourOptions = isKansaiPremiumHiaceCharter ? [10] : (config.charter_hours || [5, 8, 10]);

  useEffect(() => {
    if (isKansaiPremiumHiaceCharter && Number(form.selected_hours) !== 10) {
      setForm((prev) => ({ ...prev, selected_hours: 10 }));
    }
  }, [isKansaiPremiumHiaceCharter, form.selected_hours]);
  const showTimeAfterFlight = isTransfer && !isPointToPoint;
  const flightAirportCode = getFlightAirportCode(form, areaRecognitionRules);
  const turnstileRequired = Boolean(config.turnstile_enabled && config.turnstile_site_key);
  const canSubmit = useMemo(() => (
    form.accepted_privacy_policy
    && form.accepted_terms
    && form.accepted_cancellation_policy
    && (bookingMode !== "member" || Boolean(retailMember))
    && (!turnstileRequired || Boolean(turnstileToken))
  ), [
    bookingMode,
    retailMember,
    form.accepted_privacy_policy,
    form.accepted_terms,
    form.accepted_cancellation_policy,
    turnstileRequired,
    turnstileToken,
  ]);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token || "");
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const vehicleQuotePayload = useMemo(() => normalizePayload({
    service_type: form.service_type,
    full_name: "Guest",
    email: "guest@example.com",
    phone: "0000000000",
    transfer_subtype: form.transfer_subtype,
    departure_date: form.departure_date,
    pickup_time: form.pickup_time,
    vehicle_type: "alphard",
    pickup_area: form.pickup_area,
    dropoff_area: form.dropoff_area,
    pickup_location: form.pickup_location,
    destination: form.destination,
    flight_direction: form.flight_direction,
    flight_service_date: form.flight_service_date,
    flight_service_time: form.flight_service_time,
    flight_number: form.flight_number,
    passenger_count: form.passenger_count,
    luggage_count: form.luggage_count,
    child_seat_count: form.child_seat_count,
    infant_seat_0_3_count: form.infant_seat_0_3_count || 0,
    selected_hours: form.selected_hours,
    charter_area: form.charter_area,
    stopovers: form.stopovers,
    end_location: form.end_location,
    end_location_in_base: form.end_location_in_base,
    end_location_other: form.end_location_other,
    accepted_privacy_policy: false,
    accepted_terms: false,
    accepted_cancellation_policy: false,
  }), [
    form.service_type,
    form.transfer_subtype,
    form.departure_date,
    form.pickup_time,
    form.pickup_area,
    form.dropoff_area,
    form.pickup_location,
    form.destination,
    form.flight_direction,
    form.flight_service_date,
    form.flight_service_time,
    form.flight_number,
    form.passenger_count,
    form.luggage_count,
    form.child_seat_count,
    form.infant_seat_0_3_count,
    form.selected_hours,
    form.charter_area,
    form.stopovers,
    form.end_location,
    form.end_location_in_base,
    form.end_location_other,
  ]);
  const vehicleQuoteRequestKey = useMemo(() => JSON.stringify(vehicleQuotePayload), [vehicleQuotePayload]);

  const update = (field, value) => {
    setValidationError("");
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const updateMany = (values) => {
    setValidationError("");
    setForm((prev) => ({ ...prev, ...values }));
  };
  const handleRetailMemberChange = (member, token) => {
    setRetailMember(member);
    if (token) {
      setRetailMemberToken(token);
      localStorage.setItem("retail_member_token", token);
    }
    setBookingMode("member");
    setAuthPanelOpen(false);
    applyRetailMemberContactToForm(member, token || retailMemberToken);
  };

  const areaName = (code) => {
    const area = areaOptions.find((item) => item.code === code);
    if (!area) return code;
    return labelForLang(area, lang) || code;
  };

  const vehicleMeta = (code) => copy.vehicleMeta[code] || copy.vehicleMeta.hiace;
  const vehicleCapacity = (vehicle) => copy.vehicleCapacity[vehicle.code] || vehicle.capacity;

  useEffect(() => {
    if (!activeStepScrollReadyRef.current) {
      activeStepScrollReadyRef.current = true;
      return;
    }
    window.requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [activeStep]);

  useEffect(() => {
    setForm((prev) => {
      const next = {};
      if (prev.service_type === "transfer") {
        const subtype = prev.transfer_subtype || "arrival";
        const airportCode = getFlightAirportCode(prev, areaRecognitionRules);
        const direction = subtype === "departure" ? "departure" : "arrival";
        if (subtype !== "point_to_point" && prev.flight_direction !== direction) {
          next.flight_direction = direction;
        }
        if (prev.departure_date && prev.flight_service_date !== prev.departure_date) {
          next.flight_service_date = prev.departure_date;
        }
        if (subtype === "arrival") {
          if (airportCode) {
            if (prev.pickup_area !== airportCode) next.pickup_area = airportCode;
            if (prev.pickup_location !== airportDisplayName(airportCode)) next.pickup_location = airportDisplayName(airportCode);
          }
          const inferredDropoff = inferAreaOrOther(prev.destination, areaRecognitionRules);
          if (inferredDropoff && prev.dropoff_area !== inferredDropoff) next.dropoff_area = inferredDropoff;
        } else if (subtype === "departure") {
          if (airportCode) {
            if (prev.dropoff_area !== airportCode) next.dropoff_area = airportCode;
            if (prev.destination !== airportDisplayName(airportCode)) next.destination = airportDisplayName(airportCode);
          }
          const inferredPickup = inferAreaOrOther(prev.pickup_location, areaRecognitionRules);
          if (inferredPickup && prev.pickup_area !== inferredPickup) next.pickup_area = inferredPickup;
        } else {
          const inferredPickup = inferAreaOrOther(prev.pickup_location, areaRecognitionRules);
          const inferredDropoff = inferAreaOrOther(prev.destination, areaRecognitionRules);
          if (inferredPickup && prev.pickup_area !== inferredPickup) next.pickup_area = inferredPickup;
          if (inferredDropoff && prev.dropoff_area !== inferredDropoff) next.dropoff_area = inferredDropoff;
        }
      } else {
        const inferredPickup = inferAreaOrOther(prev.pickup_location, areaRecognitionRules);
        const inferredDropoff = inferAreaOrOther(prev.destination, areaRecognitionRules);
        if (inferredPickup && prev.pickup_area !== inferredPickup) next.pickup_area = inferredPickup;
        if (inferredDropoff && prev.dropoff_area !== inferredDropoff) next.dropoff_area = inferredDropoff;
      }
      return Object.keys(next).length ? { ...prev, ...next } : prev;
    });
  }, [
    form.service_type,
    form.transfer_subtype,
    form.departure_date,
    form.flight_number,
    form.pickup_location,
    form.destination,
    areaRecognitionRules,
  ]);

  useEffect(() => {
    const candidates = [
      { value: form.pickup_location, areaField: "pickup_area" },
      { value: form.destination, areaField: "dropoff_area" },
    ];
    const target = candidates.find((candidate) => (
      isShortGoogleMapsUrl(candidate.value)
      && !inferAreaCode(candidate.value, areaRecognitionRules)
    ));
    if (!target) return undefined;

    const mapsUrl = String(target.value || "").trim();
    if (Object.prototype.hasOwnProperty.call(mapResolutionCacheRef.current, mapsUrl)) {
      const cachedAreaCode = mapResolutionCacheRef.current[mapsUrl] || "other";
      setForm((prev) => (prev[target.areaField] === cachedAreaCode ? prev : { ...prev, [target.areaField]: cachedAreaCode }));
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/public-booking/maps/resolve`, { params: { url: mapsUrl } });
        const areaCode = response.data?.area_code || "";
        mapResolutionCacheRef.current[mapsUrl] = areaCode;
        if (!cancelled) {
          setForm((prev) => {
            if (prev.pickup_location !== mapsUrl && prev.destination !== mapsUrl) return prev;
            const nextAreaCode = areaCode || "other";
            return prev[target.areaField] === nextAreaCode ? prev : { ...prev, [target.areaField]: nextAreaCode };
          });
        }
      } catch (error) {
        mapResolutionCacheRef.current[mapsUrl] = "";
        if (!cancelled) {
          setForm((prev) => (prev[target.areaField] === "other" ? prev : { ...prev, [target.areaField]: "other" }));
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.pickup_location, form.destination, areaRecognitionRules]);

  useEffect(() => {
    if (currentStep !== "vehicle") return undefined;

    let cancelled = false;
    const fetchVehicleQuotes = async () => {
      setVehicleQuotes({});
      setVehicleQuotesLoading(true);
      setQuote(null);
      setQuoteError("");
      const entries = await Promise.all(vehicles.map(async (vehicle) => {
        try {
          const response = await axios.post(`${API}/public-booking/quote`, {
            ...vehicleQuotePayload,
            vehicle_type: vehicle.code,
          });
          return [vehicle.code, { data: response.data }];
        } catch (error) {
          return [vehicle.code, { error: true }];
        }
      }));

      if (cancelled) return;
      const nextQuotes = Object.fromEntries(entries);
      setVehicleQuotes(nextQuotes);
      setVehicleQuotesLoading(false);
    };

    fetchVehicleQuotes();
    return () => {
      cancelled = true;
    };
  }, [currentStep, vehicleQuotePayload, vehicleQuoteRequestKey, vehicles]);

  useEffect(() => {
    if (currentStep !== "vehicle") return;
    const selectedQuote = vehicleQuotes[form.vehicle_type];
    if (selectedQuote?.data) {
      setQuote(selectedQuote.data);
      setQuoteError("");
    } else if (selectedQuote?.error) {
      setQuote(null);
      setQuoteError(copy.quoteFallback);
    }
  }, [currentStep, vehicleQuotes, form.vehicle_type, copy.quoteFallback]);

  const selectVehicle = (vehicleCode) => {
    update("vehicle_type", vehicleCode);
    const selectedQuote = vehicleQuotes[vehicleCode];
    if (selectedQuote?.data) {
      setQuote(selectedQuote.data);
      setQuoteError("");
    } else if (selectedQuote?.error) {
      setQuote(null);
      setQuoteError(copy.quoteFallback);
    }
  };

  const fetchFlightLookup = async (params) => {
    const lookupBases = [API];
    if (shouldUseFlightLookupFallback()) {
      lookupBases.push(PUBLIC_FLIGHT_LOOKUP_FALLBACK_API);
    }

    let lastError;
    for (const baseUrl of lookupBases) {
      try {
        const response = await axios.get(`${baseUrl}/public-booking/flights/lookup`, { params });
        const lookup = response.data || {};
        const shouldRetryWithFallback = (
          baseUrl === API &&
          lookupBases.length > 1 &&
          (lookup.status === "needs_configuration" || lookup.error_code === "missing_api_key")
        );
        if (!shouldRetryWithFallback) {
          return lookup;
        }
      } catch (error) {
        lastError = error;
        if (baseUrl === lookupBases[lookupBases.length - 1]) {
          throw error;
        }
      }
    }
    throw lastError || new Error("Flight lookup failed");
  };

  const importFlightDetails = async () => {
    const flightNumber = String(form.flight_number || "").trim().replace(/\s+/g, "").toUpperCase();
    if (form.service_type !== "transfer" || form.transfer_subtype === "point_to_point" || flightNumber.length < 3) {
      setFlightLookup({ status: "idle" });
      return;
    }

    setFlightLookup({ status: "loading", flight_number: flightNumber });
    try {
      const lookup = await fetchFlightLookup({
        flight_number: flightNumber,
        service_date: form.departure_date,
        direction: form.transfer_subtype === "departure" ? "departure" : "arrival",
        airport_area: flightAirportCode || undefined,
        departure_airport: form.transfer_subtype === "departure" ? flightAirportCode || undefined : undefined,
        arrival_airport: form.transfer_subtype === "departure" ? undefined : flightAirportCode || undefined,
      });
      setFlightLookup(lookup);
      if (lookup.status === "found") {
        const patch = {};
        const detectedSubtype = lookup.selected_direction === "departure" ? "departure" : "arrival";
        patch.transfer_subtype = detectedSubtype;
        patch.flight_direction = detectedSubtype;
        if (lookup.selected_date_jst) {
          patch.departure_date = lookup.selected_date_jst;
          patch.flight_service_date = lookup.selected_date_jst;
        }
        if (lookup.selected_time_hhmm) patch.flight_service_time = lookup.selected_time_hhmm;
        if (lookup.suggested_pickup_time_hhmm) patch.pickup_time = lookup.suggested_pickup_time_hhmm;
        if (lookup.selected_airport_area) {
          const airportName = airportDisplayName(lookup.selected_airport_area);
          if (detectedSubtype === "arrival") {
            patch.pickup_area = lookup.selected_airport_area;
            patch.pickup_location = airportName;
          } else {
            patch.dropoff_area = lookup.selected_airport_area;
            patch.destination = airportName;
          }
        }
        if (Object.keys(patch).length) {
          updateMany(patch);
        }
      }
    } catch (error) {
      setFlightLookup({
        status: "error",
        flight_number: flightNumber,
            message: copy.flight.checkManual,
          });
    }
  };

  const getStepValidationError = () => {
    const validation = copy.validation || publicBookingCopy.en.validation;
    if (currentStep === "service" && !form.service_type) {
      return validation.service;
    }
    if (currentStep === "trip") {
      if (!form.departure_date || !form.pickup_time) {
        return validation.tripDateTime;
      }
      if (!String(form.pickup_location || "").trim() || !String(form.destination || "").trim()) {
        return validation.route;
      }
    }
    if (currentStep === "contact") {
      if (bookingMode === "member" && !retailMember) {
        return validation.member;
      }
      if (!String(form.full_name || "").trim() || !String(form.email || "").trim() || !String(form.phone || "").trim()) {
        return validation.contact;
      }
    }
    return "";
  };

  const goNext = async () => {
    const validationMessage = getStepValidationError();
    if (validationMessage) {
      setValidationError(validationMessage);
      return;
    }
    if (currentStep === "contact") {
      await calculateQuoteForReview();
      setActiveStep(4);
      return;
    }
    setValidationError("");
    setActiveStep((value) => Math.min(value + 1, steps.length - 1));
  };

  const goBack = () => setActiveStep((value) => Math.max(value - 1, 0));

  const calculateQuoteForReview = async () => {
    setLoading(true);
    setQuoteError("");
    setDuplicateRisk(null);
    try {
      const payload = normalizePayload(form);
      const [quoteResponse, duplicateResponse] = await Promise.all([
        axios.post(`${API}/public-booking/quote`, payload),
        axios.post(`${API}/public-booking/duplicate-check`, payload).catch(() => ({ data: null })),
      ]);
      setQuote(quoteResponse.data);
      setDuplicateRisk(duplicateResponse.data);
    } catch (error) {
      setQuote(null);
      setQuoteError(copy.quoteFallback);
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    if (!canSubmit) return;
    if (bookingMode === "member" && !retailMember) {
      alert("Please sign in or create a member account before submitting Member Booking.");
      return;
    }
    const duplicateText = duplicateRisk?.duplicate_risk ? "\n\nPossible duplicate booking detected. 二次确认：请确认这不是重复预约。" : "";
    if (!window.confirm(`二次确认\n${form.departure_date} ${form.pickup_time}\n${buildRouteSummary(form, areaName, copy)}${duplicateText}`)) {
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/public-booking/orders`, normalizePayload({
        ...form,
        duplicate_confirmed: Boolean(duplicateRisk?.duplicate_risk),
        turnstile_token: turnstileToken,
      }));
      window.localStorage.removeItem(PUBLIC_BOOKING_DRAFT_KEY);
      setDraftNotice(null);
      setResult(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === "duplicate_confirmation_required") {
        setDuplicateRisk(detail);
        resetTurnstile();
        alert("Possible duplicate booking detected. Please review the details and submit again only if this is not a duplicate.");
        return;
      }
      resetTurnstile();
      alert(typeof detail === "string" ? detail : detail?.message || detail?.error || copy.submitError);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const isPaymentLinkPending = result.status === "pending_payment" && !result.payment_url;
    return (
      <PublicShell lang={lang} setLang={setLang} retailMember={retailMember} onLoginClick={() => setAuthPanelOpen(true)}>
        <MemberAuthModal
          open={authPanelOpen}
          onClose={() => setAuthPanelOpen(false)}
          onMemberChange={handleRetailMemberChange}
          lang={lang}
        />
        <main className="mx-auto flex min-h-[calc(100vh-73px)] max-w-4xl items-center px-4 py-10">
          <section className="w-full rounded-[28px] border border-[#d8b769]/30 bg-[#11100d] p-6 shadow-2xl shadow-black/40 md:p-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#d8b769] text-black">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <p className="mt-6 text-sm font-semibold uppercase tracking-[0.28em] text-[#d8b769]">{copy.result.received}</p>
            <h1 className="mt-3 text-3xl font-semibold text-white md:text-5xl">{copy.result.title}</h1>
            <div className="mt-7 rounded-2xl border border-[#d8b769]/30 bg-black/40 p-6 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-white/45">{copy.result.format}</p>
              <p className="mt-3 font-mono text-4xl font-bold text-[#f3d98b] md:text-6xl">{result.confirmation_code}</p>
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-7 text-white/65">
              {copy.result.body}
              {result.status === "pending_admin_confirmation" ? copy.result.within48 : ""}
            </p>
            {isPaymentLinkPending && (
              <div className="mt-6 rounded-2xl border border-[#d8b769]/35 bg-[#d8b769]/12 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#f3d98b]">{copy.result.paymentPending}</p>
                <p className="mt-3 text-sm leading-7 text-white/70">
                  {result.payment_message || copy.result.paymentPendingBody}
                </p>
              </div>
            )}
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {result.payment_url && (
                <a href={result.payment_url} className="inline-flex items-center justify-center rounded-2xl bg-[#d8b769] px-5 py-4 font-semibold text-black">
                  <CreditCard className="mr-2 h-4 w-4" />
                  {copy.result.openPayment}
                </a>
              )}
              {result.payment_url && (
                <a
                  href={buildPaymentWhatsAppUrl(result, copy)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-2xl border border-[#d8b769]/35 bg-[#d8b769]/10 px-5 py-4 font-semibold text-[#f3d98b] hover:bg-[#d8b769]/16"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  {copy.result.whatsappPayment}
                </a>
              )}
              <a href="/booking/check" className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/8 px-5 py-4 font-semibold text-white hover:bg-white/12">
                {copy.result.checkBooking}
              </a>
            </div>
          </section>
        </main>
      </PublicShell>
    );
  }

  return (
    <PublicShell lang={lang} setLang={setLang} retailMember={retailMember} onLoginClick={() => setAuthPanelOpen(true)}>
      <MemberAuthModal
        open={authPanelOpen}
        onClose={() => setAuthPanelOpen(false)}
        onMemberChange={handleRetailMemberChange}
        lang={lang}
      />
      <main ref={wizardTopRef} className="mx-auto min-h-[calc(100vh-73px)] max-w-[860px] px-4 py-5">
        {draftNotice && (
          <div className="mb-4 rounded-[22px] border border-[#d8b769]/30 bg-[#d8b769]/10 p-4 text-white shadow-lg shadow-black/20">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-[#f3d98b]">{(bookingDraftCopy[lang] || bookingDraftCopy.en).title}</p>
                <p className="mt-1 text-xs leading-5 text-white/62">{(bookingDraftCopy[lang] || bookingDraftCopy.en).body}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={dismissBookingDraft}
                  className="rounded-full border border-white/12 px-3 py-2 text-xs font-semibold text-white/70 hover:bg-white/10"
                >
                  {(bookingDraftCopy[lang] || bookingDraftCopy.en).dismiss}
                </button>
                <button
                  type="button"
                  onClick={restoreBookingDraft}
                  className="rounded-full bg-[#d8b769] px-4 py-2 text-xs font-bold text-black hover:brightness-110"
                >
                  {(bookingDraftCopy[lang] || bookingDraftCopy.en).restore}
                </button>
              </div>
            </div>
          </div>
        )}
        <section className="overflow-hidden rounded-[34px] border border-white/10 bg-[#0d0c0a] shadow-2xl shadow-black/45">
          <div
            className="border-b border-white/10 bg-[#050505] px-5 py-5 md:px-8 md:py-6"
          >
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#d8b769]">{copy.eyebrow}</p>
                <h1 className="mt-2 max-w-2xl text-[1.45rem] font-semibold leading-tight text-white sm:text-3xl md:text-4xl">{copy.title}</h1>
                <p className="mt-2 max-w-xl text-sm leading-6 text-white/60">{copy.subtitle}</p>
              </div>
            </div>
            <StepRail activeStep={activeStep} copy={copy} />
          </div>

          <div className="p-4 md:p-5">
            {currentStep === "service" && (
              <div className="grid gap-5">
                {!lockedBookingMode && (
                  <BookingModePanel
                    bookingMode={bookingMode}
                    onChange={setBookingMode}
                    retailMember={retailMember}
                  />
                )}
                {!lockedBookingMode && bookingMode === "member" && !retailMember && (
                  <RetailMemberAuthPanel onMemberChange={handleRetailMemberChange} language={lang} />
                )}
                {bookingMode === "member" && retailMember && (
                  <div className="rounded-[24px] border border-[#d8b769]/25 bg-[#d8b769]/10 p-4 text-sm text-[#f3d98b]">
                    Signed in as {retailMember.email}. Log in to earn 3% to 5% points after completed bookings. Your points and credit can be used for your next ride.
                  </div>
                )}
                <ServiceStage form={form} update={update} copy={copy} />
              </div>
            )}

            {currentStep === "vehicle" && (
              <WizardStage
                icon={<Car className="h-5 w-5" />}
                title={copy.vehicleTitle}
                subtitle={copy.vehicleSubtitle}
              >
                <div className="grid grid-cols-3 items-stretch gap-2 sm:gap-4">
                  {vehicles.map((vehicle) => (
                    <VehicleChoiceCard
                      key={vehicle.code}
                      selected={form.vehicle_type === vehicle.code}
                      visual={cardVisuals[vehicle.code]}
                      icon={<Car className="h-6 w-6" />}
                      title={vehicle.name}
                      description={vehicleCapacity(vehicle)}
                      meta={vehicleMeta(vehicle.code)}
                      priceLabel={vehicleQuoteLabel(vehicleQuotes[vehicle.code], vehicleQuotesLoading, copy)}
                      copy={copy}
                      onClick={() => selectVehicle(vehicle.code)}
                    />
                  ))}
                </div>
                <div className="mt-5 grid gap-2 rounded-[24px] border border-white/10 bg-white/[0.035] p-2 sm:grid-cols-2">
                  <StepperNumber label={copy.passengers} value={form.passenger_count} min={1} onChange={(value) => update("passenger_count", value)} />
                  <StepperNumber label={copy.luggage} value={form.luggage_count} min={0} onChange={(value) => update("luggage_count", value)} />
                  <StepperNumber
                    label={copy.childSeats}
                    note={copy.childSeatNote}
                    value={form.child_seat_count}
                    min={0}
                    onChange={(value) => update("child_seat_count", value)}
                  />
                  <StepperNumber
                    label={copy.infantSeats}
                    note={copy.infantSeatNote}
                    value={form.infant_seat_0_3_count || 0}
                    min={0}
                    onChange={(value) => update("infant_seat_0_3_count", Math.min(10, value))}
                  />
                </div>
              </WizardStage>
            )}

            {currentStep === "trip" && (
              <WizardStage
                icon={<MapPin className="h-5 w-5" />}
                title={form.service_type === "charter" ? copy.tripTitleCharter : copy.tripTitleTransfer}
                subtitle={copy.tripSubtitle}
              >
                {form.service_type === "transfer" && (
                  <div className="mb-5 grid grid-cols-3 gap-2 sm:gap-3">
                    {transferSubtypes.map((type) => (
                      <TripSubtypeCard
                        key={type.key}
                        typeKey={type.key}
                        selected={form.transfer_subtype === type.key}
                        title={copy.serviceTypes[type.key].title}
                        description={copy.serviceTypes[type.key].description}
                        copy={copy}
                        onClick={() => updateMany(transferSubtypePatch(type.key, form, areaRecognitionRules))}
                      />
                    ))}
                  </div>
                )}

                <div className="grid gap-4">
                  <DateCalendarPicker
                    value={form.departure_date}
                    onChange={(value) => update("departure_date", value)}
                    lang={lang}
                    copy={copy}
                  />
                  {!showTimeAfterFlight && (
                    <TimeWheelPicker
                      value={form.pickup_time}
                      onChange={(value) => update("pickup_time", value)}
                      copy={copy}
                    />
                  )}
                  {form.service_type === "charter" && (
                    <>
                      <DarkField label={copy.fields.charterDepartureArea}>
                        <select value={form.pickup_area} onChange={(e) => update("pickup_area", e.target.value)} className="booking-input">
                          {areaOptions.map((area) => <option key={area.code} value={area.code}>{areaName(area.code)}</option>)}
                        </select>
                      </DarkField>
                      <DarkField label={copy.fields.charterCityArea}>
                        <select
                          value={form.dropoff_area}
                          onChange={(e) => {
                            const nextArea = e.target.value;
                            updateMany({
                              dropoff_area: nextArea,
                              end_location: nextArea,
                              end_location_in_base: ["tokyo", "haneda_airport"].includes(nextArea),
                            });
                          }}
                          className="booking-input"
                        >
                          {areaOptions.map((area) => <option key={area.code} value={area.code}>{areaName(area.code)}</option>)}
                        </select>
                      </DarkField>
                    </>
                  )}
                  {(form.service_type === "charter" || isDeparture || isPointToPoint) && (
                    lang === "ja" ? (
                      <JapaneseAddressAssistField
                        role="pickup"
                        label={copy.fields.pickupLocation}
                        form={form}
                        updateMany={updateMany}
                        placeholder={copy.placeholders.location}
                      />
                    ) : (
                      <DarkField label={copy.fields.pickupLocation}>
                        <input
                          className="booking-input"
                          value={form.pickup_location}
                          onChange={(e) => update("pickup_location", e.target.value)}
                          placeholder={copy.placeholders.location}
                        />
                      </DarkField>
                    )
                  )}
                  {form.service_type === "transfer" && !isPointToPoint && (
                    <div className="md:col-span-2">
                      <FlightRecognitionPanel
                        form={form}
                        airportCode={flightAirportCode}
                        onAirportChange={(airportCode) => {
                          const airportName = airportDisplayName(airportCode);
                          updateMany(isArrival
                            ? { pickup_area: airportCode, pickup_location: airportName }
                            : { dropoff_area: airportCode, destination: airportName });
                        }}
                        onFlightNumberChange={(value) => {
                          update("flight_number", value);
                          setFlightLookup({ status: "idle" });
                        }}
                        onFlightTimeChange={(value) => update("flight_service_time", value)}
                        onImportFlightDetails={importFlightDetails}
                        flightLookup={flightLookup}
                        lang={lang}
                        copy={copy}
                      />
                    </div>
                  )}
                  {showTimeAfterFlight && (
                    <div className="md:col-span-2">
                      <TimeWheelPicker
                        label={copy.fields.pickupTime}
                        note={copy.notes.timeAfterFlight}
                        value={form.pickup_time}
                        onChange={(value) => update("pickup_time", value)}
                        copy={copy}
                      />
                    </div>
                  )}
                  {(form.service_type === "charter" || isArrival || isPointToPoint) && (
                    lang === "ja" ? (
                      <JapaneseAddressAssistField
                        role="dropoff"
                        label={copy.fields.dropoffLocation}
                        form={form}
                        updateMany={updateMany}
                        placeholder={copy.placeholders.location}
                      />
                    ) : (
                      <DarkField label={copy.fields.dropoffLocation}>
                        <input
                          className="booking-input"
                          value={form.destination}
                          onChange={(e) => update("destination", e.target.value)}
                          placeholder={copy.placeholders.location}
                        />
                      </DarkField>
                    )
                  )}
                  <DarkField label={copy.fields.specialLuggage}>
                    <input
                      className="booking-input"
                      value={form.special_luggage}
                      onChange={(e) => update("special_luggage", e.target.value)}
                      placeholder={copy.placeholders.specialLuggage}
                    />
                  </DarkField>
                </div>

                {hasOtherArea && (
                  <OtherAreaWhatsAppPanel form={form} areaName={areaName} copy={copy} />
                )}

                {form.service_type === "charter" && (
                  <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="mb-4 text-sm font-semibold text-[#d8b769]">{copy.fields.charterDetails}</p>
                    <div className="grid gap-4">
                      <DarkField label={copy.fields.charterHours}>
                        <select value={form.selected_hours} onChange={(e) => update("selected_hours", Number(e.target.value))} className="booking-input">
                          {charterHourOptions.map((hour) => <option key={hour} value={hour}>{hour}h</option>)}
                        </select>
                      </DarkField>
                      <DarkField label={copy.fields.stopovers}><input className="booking-input" value={form.stopovers} onChange={(e) => update("stopovers", e.target.value)} placeholder={copy.placeholders.stopovers} /></DarkField>
                      <CharterNotice label={copy.fields.overtimeNote} body={copy.notes.overtime} />
                      <CharterNotice label={copy.fields.crossAreaNote} body={copy.notes.crossRegion} />
                    </div>
                  </div>
                )}
              </WizardStage>
            )}

            {currentStep === "contact" && (
              <WizardStage
                icon={<UserRound className="h-5 w-5" />}
                title={copy.contactTitle}
                subtitle={copy.contactSubtitle}
              >
                <div className="grid gap-4">
                  <ContactModeChoice
                    lang={lang}
                    bookingMode={bookingMode}
                    retailMember={retailMember}
                    onGuest={() => setBookingMode("quick")}
                    onMember={() => {
                      setBookingMode("member");
                      if (retailMember) {
                        applyRetailMemberContactToForm(retailMember, retailMemberToken);
                      }
                    }}
                  />
                  {bookingMode === "member" && retailMember ? (
                    <MemberContactSummary
                      lang={lang}
                      member={retailMember}
                      form={form}
                      update={update}
                      copy={copy}
                    />
                  ) : bookingMode === "member" ? (
                    <RetailMemberAuthPanel onMemberChange={handleRetailMemberChange} language={lang} />
                  ) : (
                    <GuestContactFields form={form} update={update} copy={copy} />
                  )}
                  <DarkField label={copy.fields.remarks}><input className="booking-input" value={form.remarks} onChange={(e) => update("remarks", e.target.value)} /></DarkField>
                </div>
              </WizardStage>
            )}

            {currentStep === "review" && (
              <WizardStage
                icon={<ShieldCheck className="h-5 w-5" />}
                title={copy.reviewTitle}
                subtitle={copy.reviewSubtitle}
              >
                <div className="grid gap-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                    <ReviewRows form={form} quote={quote} quoteError={quoteError} areaName={areaName} copy={copy} />
                  </div>
                  {duplicateRisk?.duplicate_risk && (
                    <div
                      className="rounded-2xl border border-amber-300/45 bg-amber-500/10 p-5 text-sm leading-6 text-amber-100"
                      data-testid="public-booking-duplicate-warning"
                    >
                      <p className="font-semibold text-[#f3d98b]">Possible duplicate booking / 可能是重复预约</p>
                      <p className="mt-2 text-white/70">
                        We found a similar booking with the same contact and date. Please review carefully before submitting.
                      </p>
                    </div>
                  )}
                  {!retailMember && (
                    <ReviewAuthChoice
                      lang={lang}
                      bookingMode={bookingMode}
                      onGuest={() => setBookingMode("quick")}
                      onLogin={() => {
                        setBookingMode("member");
                        setAuthPanelOpen(true);
                      }}
                    />
                  )}
                  {bookingMode === "member" && retailMember && (
                    <RetailWalletSummary
                      lang={lang}
                      member={retailMember}
                      grossTotal={quote?.price_snapshot?.retail_gross_total_price || quote?.total_price || 0}
                      creditToApply={form.retail_credit_to_apply_jpy}
                      pointsToApply={form.retail_points_to_apply}
                      onCreditChange={(value) => update("retail_credit_to_apply_jpy", value)}
                      onPointsChange={(value) => update("retail_points_to_apply", value)}
                    />
                  )}
                  <div className="rounded-2xl border border-[#d8b769]/25 bg-[#d8b769]/10 p-5">
                    <p className="text-sm font-semibold text-[#f3d98b]">{copy.review.cancellationTitle}</p>
                    <p className="mt-3 text-sm leading-7 text-white/65">
                      {copy.review.cancellationBody}
                    </p>
                    <div className="mt-5 space-y-3 text-sm">
                      <PolicyCheck label={copy.review.privacy} checked={form.accepted_privacy_policy} onChange={(value) => update("accepted_privacy_policy", value)} />
                      <PolicyCheck label={copy.review.terms} checked={form.accepted_terms} onChange={(value) => update("accepted_terms", value)} />
                      <PolicyCheck label={copy.review.cancellation} checked={form.accepted_cancellation_policy} onChange={(value) => update("accepted_cancellation_policy", value)} />
                    </div>
                    {turnstileRequired && (
                      <TurnstileVerification
                        siteKey={config.turnstile_site_key}
                        onVerify={handleTurnstileVerify}
                        onExpire={resetTurnstile}
                      />
                    )}
                    <button onClick={submitBooking} disabled={!canSubmit || loading} className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-[#d8b769] px-5 py-4 font-semibold text-black disabled:opacity-45">
                      {loading ? copy.review.submitting : copy.review.submit}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </WizardStage>
            )}

            {validationError && (
              <div className="mt-5 rounded-2xl border border-[#d8b769]/35 bg-[#d8b769]/12 px-4 py-3 text-sm font-medium text-[#f3d98b]">
                {validationError}
              </div>
            )}

            <WizardNav
              activeStep={activeStep}
              loading={loading}
              whatsappUrl={currentStep === "trip" && hasOtherArea ? buildOtherAreaWhatsAppUrl(form, areaName, copy) : ""}
              onBack={goBack}
              onNext={goNext}
              copy={copy}
            />
          </div>
        </section>

        <aside className="mt-5">
          <SummaryPanel form={form} quote={quote} quoteError={quoteError} areaName={areaName} activeStep={activeStep} copy={copy} />
        </aside>
      </main>
    </PublicShell>
  );
}

function MemberBookingGate({ lang, onMemberChange }) {
  const copy = memberGateCopy[lang] || memberGateCopy.en;

  return (
    <main className="mx-auto grid min-h-[calc(100vh-73px)] max-w-6xl items-center gap-6 px-4 py-8 lg:grid-cols-[0.94fr_1.06fr]">
      <section className="overflow-hidden rounded-[34px] border border-[#d8b769]/20 bg-[#0d0c0a] shadow-2xl shadow-black/45">
        <div className="relative h-64 overflow-hidden sm:h-80">
          <img src={publicBookingChauffeurCharter} alt="" className="h-full w-full object-cover opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/20 to-[#0d0c0a]" />
          <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#f3d98b] backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" />
            {copy.eyebrow}
          </div>
        </div>
        <div className="p-6 md:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#d8b769]">Shonan International</p>
          <h1 className="mt-3 text-3xl font-semibold leading-tight text-white md:text-5xl">{copy.title}</h1>
          <p className="mt-4 text-sm leading-7 text-white/65">{copy.body}</p>
          <div className="mt-6 grid gap-3 text-sm text-white/68">
            {copy.benefits.map((benefit) => (
              <div key={benefit} className="inline-flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#d8b769]" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          <a
            href="/book/guest"
            className="mt-7 inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/[0.06] px-5 py-3 text-sm font-semibold text-white/75 transition hover:border-[#d8b769]/45 hover:text-[#f3d98b]"
          >
            {copy.guestLink}
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </section>
      <RetailMemberAuthPanel onMemberChange={onMemberChange} language={lang} />
    </main>
  );
}

function MemberAuthModal({ open, onClose, onMemberChange, lang }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/72 px-4 py-6 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-[30px] border border-[#d8b769]/30 bg-[#080705] p-4 shadow-2xl shadow-black/60">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white/60 transition hover:text-white"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <RetailMemberAuthPanel onMemberChange={onMemberChange} language={lang} />
      </div>
    </div>
  );
}

function ContactModeChoice({ lang, bookingMode, retailMember, onGuest, onMember }) {
  const copy = contactChoiceCopy[lang] || contactChoiceCopy.en;
  const guestSelected = bookingMode !== "member";

  return (
    <section className="rounded-[26px] border border-white/10 bg-black/28 p-5">
      <p className="text-sm font-semibold text-white">{copy.title}</p>
      <p className="mt-2 text-xs leading-6 text-white/55">{copy.body}</p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={onGuest}
          className={`rounded-[22px] border p-4 text-left transition ${guestSelected ? "border-[#d8b769]/70 bg-[#d8b769]/12" : "border-white/10 bg-white/[0.035] hover:border-[#d8b769]/35"}`}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/[0.08] text-[#f3d98b]">
            <UserRound className="h-4 w-4" />
          </span>
          <span className="mt-3 block text-sm font-semibold text-white">{copy.guestTitle}</span>
          <span className="mt-1 block text-xs leading-5 text-white/55">{copy.guestBody}</span>
          <span className={`mt-4 inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${guestSelected ? "bg-[#d8b769] text-black" : "border border-white/10 text-white/55"}`}>
            {copy.guestButton}
          </span>
        </button>
        <button
          type="button"
          onClick={onMember}
          className={`relative rounded-[22px] border p-4 text-left transition ${bookingMode === "member" ? "border-[#d8b769]/70 bg-[#d8b769]/12" : "border-white/10 bg-white/[0.035] hover:border-[#d8b769]/35"}`}
        >
          <span className="absolute right-3 top-3 rounded-full border border-[#f3d98b]/45 bg-[#d8b769] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-black shadow-md shadow-[#d8b769]/20">
            {copy.recommendedBadge}
          </span>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
            <WalletCards className="h-4 w-4" />
          </span>
          <span className="mt-3 block text-sm font-semibold text-white">{copy.memberTitle}</span>
          <span className="mt-1 block text-xs leading-5 text-white/55">{copy.memberBody}</span>
          <span className={`mt-4 inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${bookingMode === "member" ? "bg-[#d8b769] text-black" : "border border-[#d8b769]/50 text-[#f3d98b]"}`}>
            {retailMember ? copy.memberActiveButton : copy.memberButton}
          </span>
        </button>
      </div>
    </section>
  );
}

function CharterNotice({ label, body }) {
  return (
    <div className="rounded-[20px] border border-[#d8b769]/20 bg-black/24 px-4 py-3">
      <p className="text-sm font-semibold text-white/72">{label}</p>
      <p className="mt-2 whitespace-normal break-words text-sm leading-7 text-white/64">
        {body}
      </p>
    </div>
  );
}

function GuestContactFields({ form, update, copy }) {
  return (
    <div className="grid gap-4">
      <DarkField label={copy.fields.fullName}><input className="booking-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} /></DarkField>
      <DarkField label={copy.fields.email}><input className="booking-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></DarkField>
      <PhoneField form={form} update={update} label={copy.fields.phone} />
    </div>
  );
}

function MemberContactSummary({ lang, member, form, update, copy }) {
  const contactCopy = contactChoiceCopy[lang] || contactChoiceCopy.en;
  const needsFullName = !String(form.full_name || member?.full_name || "").trim();
  const needsPhone = !String(form.phone || member?.phone || "").trim();

  return (
    <section className="rounded-[26px] border border-[#d8b769]/25 bg-[#d8b769]/10 p-5">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
          <UserRound className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm font-semibold text-[#f3d98b]">{contactCopy.memberInfoTitle}</p>
          <p className="mt-1 text-xs leading-5 text-white/58">{contactCopy.memberInfoBody}</p>
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        <ContactInfoRow label={copy.fields.fullName} value={form.full_name || member?.full_name || contactCopy.notSaved} />
        <ContactInfoRow label={copy.fields.email} value={form.email || member?.email || contactCopy.notSaved} />
        <ContactInfoRow label={copy.fields.phone} value={form.phone || member?.phone || contactCopy.notSaved} />
      </div>
      {(needsFullName || needsPhone) && (
        <div className="mt-4 grid gap-3">
          {needsFullName && (
            <DarkField label={copy.fields.fullName}>
              <input className="booking-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} />
            </DarkField>
          )}
          {needsPhone && (
            <PhoneField form={form} update={update} label={copy.fields.phone} />
          )}
        </div>
      )}
    </section>
  );
}

function PhoneField({ form, update, label }) {
  return (
    <DarkField label={label}>
      <div className="grid gap-2 sm:grid-cols-[190px_1fr]">
        <select
          className="booking-input"
          value={form.phone_country_code || "JP"}
          onChange={(event) => update("phone_country_code", event.target.value)}
          aria-label="Phone country code"
        >
          {phoneCountryOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <input
          className="booking-input"
          inputMode="tel"
          value={form.phone}
          onChange={(event) => update("phone", event.target.value)}
          placeholder="Phone number"
        />
      </div>
    </DarkField>
  );
}

function ContactInfoRow({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/24 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/38">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-white">{value}</p>
    </div>
  );
}

function ReviewAuthChoice({ lang, bookingMode, onGuest, onLogin }) {
  const copy = reviewAuthChoiceCopy[lang] || reviewAuthChoiceCopy.en;
  const guestSelected = bookingMode !== "member";

  return (
    <section className="rounded-[26px] border border-white/10 bg-black/28 p-5">
      <p className="text-sm font-semibold text-white">{copy.title}</p>
      <p className="mt-2 text-xs leading-6 text-white/55">{copy.body}</p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={onGuest}
          className={`rounded-[22px] border p-4 text-left transition ${guestSelected ? "border-[#d8b769]/70 bg-[#d8b769]/12" : "border-white/10 bg-white/[0.035] hover:border-[#d8b769]/35"}`}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/[0.08] text-[#f3d98b]">
            <UserRound className="h-4 w-4" />
          </span>
          <span className="mt-3 block text-sm font-semibold text-white">{copy.guestTitle}</span>
          <span className="mt-1 block text-xs leading-5 text-white/55">{copy.guestBody}</span>
          <span className={`mt-4 inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${guestSelected ? "bg-[#d8b769] text-black" : "border border-white/10 text-white/55"}`}>
            {copy.guestButton}
          </span>
        </button>
        <button
          type="button"
          onClick={onLogin}
          className="rounded-[22px] border border-[#d8b769]/35 bg-[#d8b769]/8 p-4 text-left transition hover:border-[#d8b769]/70 hover:bg-[#d8b769]/14"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
            <WalletCards className="h-4 w-4" />
          </span>
          <span className="mt-3 block text-sm font-semibold text-white">{copy.memberTitle}</span>
          <span className="mt-1 block text-xs leading-5 text-white/55">{copy.memberBody}</span>
          <span className="mt-4 inline-flex rounded-full border border-[#d8b769]/50 px-3 py-1.5 text-xs font-bold text-[#f3d98b]">
            {copy.memberButton}
          </span>
        </button>
      </div>
    </section>
  );
}

function normalizePayload(form) {
  const phone = String(form.phone || "").trim();
  const country = phoneCountryOptions.find((option) => option.value === (form.phone_country_code || "JP"));
  return {
    ...form,
    phone: phone && !phone.startsWith("+") ? `${country?.dialCode || "+81"} ${phone}` : phone,
    stopovers: typeof form.stopovers === "string" ? form.stopovers.split(",").map((item) => item.trim()).filter(Boolean) : form.stopovers,
  };
}

function BookingModePanel({ bookingMode, onChange, retailMember }) {
  const options = [
    {
      key: "quick",
      title: "Quick Booking",
      body: "Book without creating an account.",
      foot: "Fast checkout for one-time guests.",
    },
    {
      key: "member",
      title: "Member Booking",
      body: "Earn 3% to 5% points on completed rides.",
      foot: "Your points and credit can be used for your next ride.",
    },
  ];

  return (
    <section className="grid gap-3 sm:grid-cols-2">
      {options.map((option) => {
        const selected = bookingMode === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            className={`rounded-[26px] border p-5 text-left transition ${
              selected
                ? "border-[#d8b769] bg-[#d8b769]/12 shadow-[0_0_0_1px_rgba(216,183,105,0.35)]"
                : "border-white/10 bg-white/[0.035] hover:border-[#d8b769]/45"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-lg font-semibold text-white">{option.title}</p>
                <p className="mt-2 text-sm leading-6 text-white/65">{option.body}</p>
              </div>
              {selected && <span className="rounded-full bg-[#d8b769] px-3 py-1 text-xs font-bold text-black">Selected</span>}
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#d8b769]">{option.foot}</p>
            {option.key === "member" && retailMember && (
              <p className="mt-3 truncate text-xs text-white/50">{retailMember.email}</p>
            )}
          </button>
        );
      })}
    </section>
  );
}

function vehicleQuoteLabel(entry, loading, copy) {
  const totalPrice = entry?.data?.total_price;
  if (totalPrice) return `¥${Number(totalPrice).toLocaleString()}`;
  if (entry?.data || entry?.error) return copy.vehicleQuote?.pending || copy.quoteFallback;
  if (loading) return copy.vehicleQuote?.checking || "";
  return copy.vehicleQuote?.waiting || "";
}

function normalizeAreaSearchText(value) {
  const rawValue = String(value || "");
  let decoded = rawValue;
  for (let index = 0; index < 2; index += 1) {
    try {
      const next = decodeURIComponent(decoded.replace(/\+/g, " "));
      if (next === decoded) break;
      decoded = next;
    } catch (error) {
      break;
    }
  }
  return decoded
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[‐‑‒–—―ー]/g, "-")
    .replace(/[、，]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractGoogleMapsSearchText(value) {
  const original = String(value || "").trim();
  if (!original) return "";
  const normalized = normalizeAreaSearchText(original);
  if (!/(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(original)) {
    return normalized;
  }

  const parts = [normalized];
  try {
    const url = new URL(original);
    ["q", "query", "destination", "daddr", "address", "cid"].forEach((key) => {
      const param = url.searchParams.get(key);
      if (param) parts.push(param);
    });
    url.pathname
      .split("/")
      .filter(Boolean)
      .forEach((segment) => {
        if (!["maps", "place", "search", "dir", "data"].includes(segment.toLowerCase()) && !segment.startsWith("@")) {
          parts.push(segment);
        }
      });
  } catch (error) {
    parts.push(original);
  }

  return normalizeAreaSearchText(parts.join(" "));
}

function isGoogleMapsUrl(value) {
  return /(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(String(value || ""));
}

function isShortGoogleMapsUrl(value) {
  return /(maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(String(value || ""));
}

function areaKeywordList(area) {
  return [
    area.code,
    area.name,
    area.name_en,
    area.name_zh,
    area.name_zhHant,
    area.name_zh_hant,
    area.name_ja,
    ...(area.aliases || []),
    ...(area.keywords || []),
  ].filter(Boolean);
}

function buildAreaRecognitionRules(areaOptions = FALLBACK_AREAS) {
  const merged = new Map();
  const addKeyword = (code, keyword) => {
    const normalized = normalizeAreaSearchText(keyword);
    if (!code || code === "other" || !normalized) return;
    if (!merged.has(code)) merged.set(code, new Set());
    merged.get(code).add(normalized);
  };

  areaOptions.forEach((area) => areaKeywordList(area).forEach((keyword) => addKeyword(area.code, keyword)));
  staticAreaAliases.forEach((rule) => rule.keywords.forEach((keyword) => addKeyword(rule.code, keyword)));

  return Array.from(merged.entries()).map(([code, keywordSet]) => ({
    code,
    keywords: Array.from(keywordSet).sort((left, right) => right.length - left.length),
  }));
}

function inferAreaCode(value, recognitionRules = buildAreaRecognitionRules(FALLBACK_AREAS)) {
  const textValue = extractGoogleMapsSearchText(value);
  if (!textValue) return "";
  const exactMatch = recognitionRules.find((rule) => rule.keywords.some((keyword) => textValue === keyword));
  if (exactMatch) return exactMatch.code;
  const containsMatch = recognitionRules.find((rule) => (
    rule.keywords.some((keyword) => keyword.length >= 2 && textValue.includes(keyword))
  ));
  return containsMatch?.code || "";
}

function inferAreaOrOther(value, recognitionRules) {
  const textValue = normalizeAreaSearchText(value);
  if (!textValue) return "";
  const inferred = inferAreaCode(textValue, recognitionRules);
  if (inferred) return inferred;
  return isGoogleMapsUrl(value) ? "" : "other";
}

function isAirportArea(code) {
  return ["narita_airport", "haneda_airport", "kansai_airport", "itami_airport"].includes(code);
}

function airportDisplayName(code, lang = "en") {
  const names = {
    haneda_airport: { en: "Haneda Airport", ja: "羽田空港", zh: "羽田机场", zhHant: "羽田機場" },
    itami_airport: { en: "Itami Airport", ja: "伊丹空港", zh: "伊丹机场", zhHant: "伊丹機場" },
    kansai_airport: { en: "Kansai Airport", ja: "関西空港", zh: "关西机场", zhHant: "關西機場" },
    narita_airport: { en: "Narita Airport", ja: "成田空港", zh: "成田机场", zhHant: "成田機場" },
  };
  if (names[code]) return names[code][lang] || names[code].en;
  return "";
}

function getFlightAirportCode(form, recognitionRules) {
  const flightText = String(form.flight_number || "").trim();
  const flightAirport = inferAreaCode(flightText, recognitionRules);
  if (isAirportArea(flightAirport)) return flightAirport;
  if (form.transfer_subtype === "arrival" && isAirportArea(form.pickup_area)) return form.pickup_area;
  if (form.transfer_subtype === "departure" && isAirportArea(form.dropoff_area)) return form.dropoff_area;
  const locationAirport = inferAreaCode(`${form.pickup_location || ""} ${form.destination || ""}`, recognitionRules);
  if (isAirportArea(locationAirport)) return locationAirport;
  return "narita_airport";
}

function departureRecommendation(airportCode, copy = publicBookingCopy.en) {
  if (airportCode === "haneda_airport") {
    return copy.recommendation.haneda;
  }
  if (airportCode === "itami_airport") {
    return copy.recommendation.itami;
  }
  if (airportCode === "kansai_airport") {
    return copy.recommendation.kansai;
  }
  if (airportCode === "narita_airport") {
    return copy.recommendation.narita;
  }
  return "";
}

function transferSubtypeLabel(subtype, copy = publicBookingCopy.en) {
  return copy.serviceTypes[subtype]?.label || copy.serviceTypes.fallback;
}

function transferSubtypePatch(subtype, form, recognitionRules) {
  const airportCode = getFlightAirportCode({ ...form, transfer_subtype: subtype }, recognitionRules);
  const airportName = airportDisplayName(airportCode);
  const base = {
    transfer_subtype: subtype,
    flight_direction: subtype === "departure" ? "departure" : "arrival",
    flight_service_time: "",
  };
  if (subtype === "arrival") {
    return {
      ...base,
      pickup_area: airportCode,
      pickup_location: airportName,
      destination: form.destination === airportDisplayName(form.dropoff_area) ? "" : form.destination,
    };
  }
  if (subtype === "departure") {
    return {
      ...base,
      dropoff_area: airportCode,
      destination: airportName,
      pickup_location: form.pickup_location === airportDisplayName(form.pickup_area) ? "" : form.pickup_location,
    };
  }
  return {
    ...base,
    pickup_area: "tokyo",
    dropoff_area: "tokyo",
    pickup_location: isAirportArea(inferAreaCode(form.pickup_location, recognitionRules)) ? "" : form.pickup_location,
    destination: isAirportArea(inferAreaCode(form.destination, recognitionRules)) ? "" : form.destination,
  };
}

function buildRouteSummary(form, areaName, copy = publicBookingCopy.en) {
  if (form.service_type === "charter") {
    return `${form.pickup_location || areaName(form.pickup_area)} → ${form.destination || areaName(form.dropoff_area)}`;
  }
  const lang = Object.entries(publicBookingCopy).find(([, value]) => value === copy)?.[0] || "en";
  const airport = airportDisplayName(getFlightAirportCode(form), lang);
  if (form.transfer_subtype === "arrival") {
    return `${airport || copy.route.airport} → ${form.destination || copy.route.addDropoff}`;
  }
  if (form.transfer_subtype === "departure") {
    return `${form.pickup_location || copy.route.addPickup} → ${airport || copy.route.airport}`;
  }
  if (form.pickup_location || form.destination) {
    return `${form.pickup_location || copy.route.addPickup} → ${form.destination || copy.route.addDropoff}`;
  }
  return copy.route.addBoth;
}

function buildFlightSummary(form, copy = publicBookingCopy.en) {
  const lang = Object.entries(publicBookingCopy).find(([, value]) => value === copy)?.[0] || "en";
  const parts = [
    form.flight_number || copy.flight.timePending,
    airportDisplayName(getFlightAirportCode(form), lang),
    form.flight_service_time || copy.flight.flightTimePending,
  ].filter(Boolean);
  return parts.join(" · ");
}

function PublicShell({ lang, setLang, retailMember = null, onLoginClick, children }) {
  const actionCopy = shellActionCopy[lang] || shellActionCopy.en;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <OpeningLanguageGate />
      <style>{`
        .booking-input{width:100%;border-radius:16px;border:1px solid rgba(255,255,255,.12);background:#202838!important;padding:.9rem 1rem;font-size:.95rem;color:#f8fafc!important;-webkit-text-fill-color:#f8fafc;caret-color:#f8fafc;min-height:52px;color-scheme:dark}
        input.booking-input,select.booking-input,textarea.booking-input{background-color:#202838!important;color:#f8fafc!important;-webkit-text-fill-color:#f8fafc!important;color-scheme:dark}
        .booking-input-compact{min-height:44px;border-radius:14px;padding:.65rem .8rem;font-size:.92rem}
        .booking-input:focus{outline:2px solid rgba(216,183,105,.28);border-color:rgba(216,183,105,.72);background:#263247!important}
        .booking-input::placeholder{color:rgba(248,250,252,.46);-webkit-text-fill-color:rgba(248,250,252,.46)}
        .booking-input option{background:#111827;color:#f8fafc}
        .booking-input:-webkit-autofill,.booking-input:-webkit-autofill:hover,.booking-input:-webkit-autofill:focus{box-shadow:0 0 0 1000px #202838 inset!important;-webkit-box-shadow:0 0 0 1000px #202838 inset!important;-webkit-text-fill-color:#f8fafc!important;caret-color:#f8fafc;color-scheme:dark}
        .mini-time-wheel{scrollbar-width:none;scroll-snap-type:y proximity}
        .mini-time-wheel::-webkit-scrollbar{display:none}
        .mini-time-wheel button{scroll-snap-align:center}
      `}</style>
      <header className="border-b border-white/10 bg-[#050505]">
        <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5">
          <PublicBrandMark href="/book" />
          <div className="flex items-center justify-end gap-2">
            <PublicLanguageSwitcher
              options={bookingLanguageOptions}
              activeCode={lang}
              onChange={(item) => setLang(item.code)}
              className="max-w-[min(100vw-1.5rem,440px)]"
            />
            {retailMember ? (
              <a href="/book/mypage" className="inline-flex max-w-[170px] items-center truncate rounded-full border border-[#d8b769]/30 bg-[#d8b769]/10 px-3 py-1.5 text-xs font-semibold text-[#f3d98b] transition hover:bg-[#d8b769] hover:text-black sm:max-w-[220px]">
                My Page · {retailMember.email}
              </a>
            ) : (
              onLoginClick && (
                <button
                  type="button"
                  onClick={onLoginClick}
                  className="rounded-full border border-[#d8b769]/35 bg-[#d8b769]/10 px-3 py-1.5 text-xs font-bold text-[#f3d98b] transition hover:bg-[#d8b769] hover:text-black"
                >
                  {actionCopy.login}
                </button>
              )
            )}
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function ServiceStage({ form, update, copy }) {
  return (
    <div className="mx-auto w-full max-w-[780px] overflow-hidden">
      <div className="mb-4 text-center sm:mb-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#d8b769] sm:text-xs sm:tracking-[0.22em]">{copy.serviceStep}</p>
        <h2 className="mt-2 text-2xl font-light leading-tight text-white sm:text-3xl md:text-5xl">{copy.chooseService}</h2>
        <p className="mt-2 text-sm font-semibold text-white/62 sm:text-base md:text-xl">{copy.chooseServiceSubtitle}</p>
      </div>

      <div className="grid w-full grid-cols-2 gap-3 sm:gap-5">
        <ServiceImageCard
          selected={form.service_type === "charter"}
          image={cardVisuals.charter.image}
          imagePosition={cardVisuals.charter.position}
          title={copy.charterTitle}
          description={copy.charterDescription}
          meta={copy.charterMeta}
          icon={<CalendarClock className="h-5 w-5" />}
          copy={copy}
          onClick={() => update("service_type", "charter")}
        />
        <ServiceImageCard
          selected={form.service_type === "transfer"}
          image={cardVisuals.transfer.image}
          imagePosition={cardVisuals.transfer.position}
          title={copy.transferTitle}
          description={copy.transferDescription}
          meta={copy.transferMeta}
          icon={<MapPin className="h-5 w-5" />}
          copy={copy}
          onClick={() => update("service_type", "transfer")}
        />
      </div>
    </div>
  );
}

function ServiceImageCard({ selected, image, imagePosition, title, description, meta, icon, onClick, copy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex min-w-0 flex-col overflow-hidden rounded-[20px] border-2 bg-[#080807] text-center transition sm:min-h-[470px] sm:rounded-[26px] ${
        selected
          ? "border-[3px] border-[#d8b769] shadow-2xl shadow-[#d8b769]/25"
          : "border-[#b7954e]/70 hover:border-[#d8b769]"
      }`}
    >
      {selected && (
        <span className="absolute right-2.5 top-2.5 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#d8b769] text-black shadow-lg shadow-black/30 sm:right-4 sm:top-4 sm:h-10 sm:w-10">
          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" />
        </span>
      )}
      <div className="relative h-[150px] overflow-hidden sm:h-[250px]">
        <img
          src={image}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: imagePosition || "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 via-[55%] to-[#080807]" />
      </div>
      <div className="flex flex-1 flex-col items-center justify-between px-2.5 py-4 sm:px-5 sm:py-6">
        <div className="min-w-0">
          <h3 className="break-words text-[18px] font-bold tracking-[0.11em] text-[#f3d98b] sm:text-3xl sm:tracking-[0.12em] md:text-4xl">{title}</h3>
          <p className="mt-3 text-[13px] font-semibold leading-6 text-white/78 sm:mt-4 sm:text-xl sm:leading-relaxed md:text-2xl">{description}</p>
        </div>
        <div className="mt-4 w-full space-y-2 sm:mt-6 sm:space-y-3">
          {selected && (
            <div className="mx-auto w-fit rounded-full bg-[#d8b769] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-black sm:px-4 sm:py-1.5 sm:text-sm">
              {copy.selected}
            </div>
          )}
          <div className="mx-auto flex max-w-[140px] flex-col items-center justify-center gap-2 text-center text-[12px] font-semibold leading-5 text-white/78 sm:inline-flex sm:max-w-none sm:flex-row sm:gap-3 sm:text-base md:text-lg">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#d8b769] text-[#d8b769] sm:h-10 sm:w-10">
              {icon}
            </span>
            <span>{meta}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function StepRail({ activeStep, copy }) {
  const progress = steps.length > 1 ? (activeStep / (steps.length - 1)) * 100 : 0;
  return (
    <div className="mt-5">
      <div className="relative px-1">
        <div className="absolute left-6 right-6 top-4 h-px bg-white/12" />
        <div
          className="absolute left-6 top-4 h-px bg-[#d8b769] transition-all duration-300"
          style={{ width: `calc((100% - 3rem) * ${progress / 100})` }}
        />
        <div className="relative grid grid-cols-5 gap-1">
          {steps.map((step, index) => {
            const done = index < activeStep;
            const current = index === activeStep;
            return (
              <div key={step.key} className="flex min-w-0 flex-col items-center gap-2 text-center">
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition ${
                  current
                    ? "border-[#d8b769] bg-[#d8b769] text-black shadow-lg shadow-[#d8b769]/20"
                    : done
                      ? "border-[#d8b769] bg-[#d8b769]/18 text-[#f3d98b]"
                      : "border-white/12 bg-[#0d0c0a] text-white/38"
                }`}>
                  {index + 1}
                </span>
                <span className={`w-full truncate text-[10px] font-semibold sm:text-[11px] ${
                  current ? "text-[#f3d98b]" : done ? "text-white/70" : "text-white/35"
                }`}>
                  {copy.steps[index] || step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WizardStage({ icon, title, subtitle, children }) {
  return (
    <div>
      <div className="mb-6 flex items-start gap-4">
        <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#d8b769]/35 bg-[#d8b769]/10 text-[#d8b769]">
          {icon}
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-white md:text-4xl">{title}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-white/56">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function ChoiceCard({ selected, icon, visual, title, description, meta, onClick, copy }) {
  const hasVisual = Boolean(visual?.image);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative h-full overflow-hidden rounded-[24px] border-2 transition sm:rounded-[28px] ${hasVisual ? "min-h-[360px] text-left" : "min-h-[210px] bg-[#080807] p-3 text-center sm:p-5"} ${
        selected
          ? "border-[3px] border-[#d8b769] bg-[#d8b769]/13 shadow-xl shadow-[#d8b769]/16"
          : "border-[#b7954e]/60 bg-[#080807] hover:border-[#d8b769]"
      }`}
    >
      {!hasVisual && selected && (
        <span className="absolute right-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#d8b769] text-black shadow-lg shadow-black/30 sm:right-4 sm:top-4 sm:h-9 sm:w-9">
          <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />
        </span>
      )}
      {hasVisual && (
        <>
          <div
            className="absolute inset-0 bg-cover"
            style={{ backgroundImage: `url(${visual.image})`, backgroundPosition: visual.position || "center" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/30 to-black/90" />
          <div className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-b from-transparent via-[#080807]/70 to-[#080807] backdrop-blur-[5px]" />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 p-4">
            <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl backdrop-blur ${selected ? "bg-[#d8b769] text-black" : "bg-black/45 text-[#d8b769]"}`}>
              {icon}
            </span>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold backdrop-blur ${selected ? "bg-[#d8b769] text-black" : "bg-black/45 text-white/72"}`}>
              {selected ? copy.selected : copy.choose}
            </span>
          </div>
        </>
      )}
      <div className={`relative z-10 flex flex-col ${hasVisual ? "min-h-[360px] justify-end p-5" : "h-full items-center justify-between"}`}>
        {!hasVisual && (
          <div className="flex w-full items-center justify-center">
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl sm:h-12 sm:w-12 ${selected ? "bg-[#d8b769] text-black" : "bg-white/8 text-[#d8b769]"}`}>
              {icon}
            </span>
          </div>
        )}
        <div className={!hasVisual ? "mt-4" : ""}>
          <h3 className={`${hasVisual ? "text-xl" : "break-words text-[13px] tracking-[0.04em] sm:text-xl sm:tracking-[0.06em] lg:text-2xl lg:tracking-[0.08em]"} font-semibold leading-tight text-[#f3d98b]`}>{title}</h3>
          <p className="mt-3 text-[11px] font-semibold leading-5 text-white/72 sm:text-sm sm:leading-6">{description}</p>
          <p className="mt-4 text-[11px] font-semibold leading-5 text-[#f3d98b] sm:text-sm">{meta}</p>
        </div>
        {!hasVisual && selected && (
          <div className="mt-4 rounded-full bg-[#d8b769] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-black sm:mt-5 sm:px-4 sm:py-1.5 sm:text-xs sm:tracking-[0.12em]">
            {copy.selected}
          </div>
        )}
      </div>
    </button>
  );
}

function VehicleChoiceCard({ selected, icon, visual, title, description, meta, priceLabel, onClick, copy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative h-full min-h-[322px] overflow-hidden rounded-[22px] border-2 bg-[#080807] text-left transition sm:min-h-[346px] sm:rounded-[26px] ${
        selected
          ? "border-[3px] border-[#d8b769] shadow-xl shadow-[#d8b769]/16"
          : "border-[#b7954e]/55 hover:border-[#d8b769]"
      }`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(216,183,105,0.18),transparent_45%),linear-gradient(180deg,#15110b_0%,#050505_56%,#080807_100%)]" />
      {visual?.image && (
        <div
          className="absolute inset-x-0 top-0 h-[70%] bg-cover transition duration-500 group-hover:scale-[1.025]"
          style={{
            backgroundImage: `url(${visual.image})`,
            backgroundPosition: visual.position || "center",
          }}
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/12 via-black/12 to-[#080807]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-[30%] h-[36%] bg-gradient-to-b from-transparent via-[#080807]/76 to-[#080807]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] bg-[#080807]" />
      <span className={`absolute left-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full backdrop-blur sm:left-3 sm:top-3 sm:h-8 sm:w-8 ${
        selected ? "bg-[#d8b769] text-black" : "bg-black/55 text-[#d8b769]"
      }`}>
        {icon}
      </span>
      {selected && (
        <span className="absolute right-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#d8b769] text-black shadow-lg shadow-black/30 sm:right-3 sm:top-3 sm:h-8 sm:w-8">
          <CheckCircle2 className="h-4 w-4" />
        </span>
      )}

      <div className="relative z-10 flex h-full flex-col justify-end p-3 text-center sm:p-4">
        <div className="flex min-h-[130px] flex-col justify-between sm:min-h-[138px]">
        <div>
          <h3 className="break-words text-[12px] font-semibold leading-tight text-[#f3d98b] drop-shadow sm:text-[15px]">
            {title}
          </h3>
          <p className="mt-2 text-[10px] font-semibold leading-4 text-white/82 sm:text-xs sm:leading-5">
            {description}
          </p>
          <p className="mt-2 text-[10px] font-semibold leading-4 text-[#f3d98b]/90 sm:text-xs sm:leading-5">
            {meta}
          </p>
          {priceLabel && (
            <p className="mt-3 text-[14px] font-bold leading-none text-white drop-shadow sm:text-base">
              {priceLabel}
            </p>
          )}
        </div>
        <div className={`mx-auto mt-3 rounded-full px-2 py-1 text-[9px] font-bold uppercase tracking-[0.1em] sm:px-3 sm:text-[10px] ${
          selected ? "bg-[#d8b769] text-black" : "border border-[#d8b769]/30 text-[#f3d98b]/72"
        }`}>
          {selected ? copy.selected : copy.choose}
        </div>
        </div>
      </div>
    </button>
  );
}

function TripSubtypeCard({ typeKey, selected, title, description, onClick, copy }) {
  const Icon = typeKey === "point_to_point" ? MapPin : Plane;
  const visual = cardVisuals[typeKey];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative min-h-[232px] overflow-hidden rounded-[22px] border text-left transition sm:min-h-[252px] ${
        selected
          ? "border-[#d8b769] bg-[#d8b769]/14 shadow-xl shadow-[#d8b769]/12"
          : "border-white/10 bg-white/[0.035] hover:border-[#d8b769]/45 hover:bg-white/[0.055]"
      }`}
    >
      {visual?.image && (
        <div
          className="absolute inset-x-0 top-0 h-[70%] bg-cover transition duration-500 group-hover:scale-[1.03]"
          style={{
            backgroundImage: `url(${visual.image})`,
            backgroundPosition: visual.position,
          }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/4 via-black/18 via-[48%] to-[#070604] to-[80%]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(243,217,139,0.16),transparent_42%)]" />
      <div className="absolute left-2 top-2 z-10 flex items-center justify-between gap-2 right-2">
        <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${
          selected ? "bg-[#d8b769] text-black" : "bg-black/35 text-[#d8b769]"
        }`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        {selected && (
          <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d8b769] text-black shadow-md shadow-[#d8b769]/20">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      <div className="relative z-10 flex min-h-[232px] flex-col justify-end p-2.5 text-center sm:min-h-[252px] sm:p-3">
        <div className="flex min-h-[90px] flex-col justify-between sm:min-h-[100px]">
          <div>
            <p className="break-words text-[14px] font-black leading-tight tracking-[0.02em] text-[#ffd76b] drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] sm:text-[17px]">
              {title}
            </p>
            <div className="mx-auto mt-2 h-px w-9 bg-[#d8b769]/50" />
            <p className="mt-2 line-clamp-2 text-[9px] font-medium leading-[1.4] text-white/62 drop-shadow sm:text-[10.5px]">
              {description}
            </p>
          </div>
          <span className={`mx-auto mt-2 inline-flex w-fit rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${
            selected ? "bg-[#d8b769] text-black" : "border border-[#d8b769]/24 text-[#f3d98b]/64"
          }`}>
            {selected ? copy.selected : copy.choose}
          </span>
        </div>
      </div>
    </button>
  );
}

function StepperNumber({ label, note, value, min = 0, onChange }) {
  return (
    <div className="rounded-[18px] bg-black/20 px-3 py-3 ring-1 ring-white/[0.06] transition hover:bg-white/[0.045]">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold text-white/64">{label}</p>
          {note && <p className="mt-1 line-clamp-2 text-[10px] leading-4 text-[#f3d98b]/78">{note}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-[#11100d] p-1">
          <button
            type="button"
            onClick={() => onChange(Math.max(min, Number(value) - 1))}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-white/62 transition hover:bg-white/10 hover:text-white"
            aria-label={`Decrease ${label}`}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[22px] text-center text-xl font-semibold leading-none text-white">{value}</span>
          <button
            type="button"
            onClick={() => onChange(Number(value) + 1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#d8b769] text-black transition hover:brightness-110"
            aria-label={`Increase ${label}`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const japaneseAddressFieldMap = {
  pickup: {
    location: "pickup_location",
    detail: "pickup_address_detail",
    postalCode: "pickup_postal_code",
    prefecture: "pickup_prefecture",
    city: "pickup_city",
    street: "pickup_street",
    building: "pickup_building",
    mapsUrl: "pickup_maps_url",
  },
  dropoff: {
    location: "destination",
    detail: "dropoff_address_detail",
    postalCode: "dropoff_postal_code",
    prefecture: "dropoff_prefecture",
    city: "dropoff_city",
    street: "dropoff_street",
    building: "dropoff_building",
    mapsUrl: "dropoff_maps_url",
  },
};

function buildJapaneseAddressDetail(parts) {
  const main = `${parts.prefecture || ""}${parts.city || ""}${parts.street || ""}`.trim();
  return [parts.postalCode ? `〒${parts.postalCode}` : "", main, parts.building || ""]
    .filter(Boolean)
    .join(" ");
}

function normalizeJapanesePostalCodeInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 7);
  return digits.length === 7 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
}

function extractJapanesePostalCode(value) {
  const match = normalizeJapaneseAddressText(value).match(/\b(\d{3})-?(\d{4})\b/);
  return match ? `${match[1]}-${match[2]}` : "";
}

const japanesePrefectures = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県",
  "岐阜県", "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府",
  "兵庫県", "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県",
  "広島県", "山口県", "徳島県", "香川県", "愛媛県", "高知県",
  "福岡県", "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県",
  "鹿児島県", "沖縄県",
];

const japanesePostalCodeFallbacks = {
  "100-0005": { prefecture: "東京都", city: "千代田区", street: "丸の内" },
  "125-0042": { prefecture: "東京都", city: "葛飾区", street: "金町" },
  "125-0024": { prefecture: "東京都", city: "葛飾区", street: "新小岩" },
  "160-0023": { prefecture: "東京都", city: "新宿区", street: "西新宿" },
};

function normalizeJapaneseAddressText(value) {
  let text = String(value || "");
  try {
    text = decodeURIComponent(text.replace(/\+/g, " "));
  } catch (error) {
    // Keep the original input when it is not URL encoded.
  }
  return text
    .replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
    .replace(/[－ー―−]/g, "-")
    .replace(/〒/g, " ")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function splitJapaneseStreetAndBuilding(value) {
  const text = normalizeJapaneseAddressText(value);
  if (!text) return { street: "", building: "" };
  const match = text.match(/^(.+?(?:\d+丁目)?\d+(?:-\d+){0,4})(?:\s+(.+)|(.+))?$/);
  if (!match) return { street: text, building: "" };
  return {
    street: normalizeJapaneseAddressText(match[1]),
    building: normalizeJapaneseAddressText(match[2] || match[3] || ""),
  };
}

function parseJapaneseAddressLocally(value) {
  let text = normalizeJapaneseAddressText(value);
  const postalCode = extractJapanesePostalCode(text);
  const postalMatch = postalCode ? text.match(/\b(\d{3})-?(\d{4})\b/) : null;
  if (postalMatch) {
    text = normalizeJapaneseAddressText(`${text.slice(0, postalMatch.index)} ${text.slice(postalMatch.index + postalMatch[0].length)}`);
  }

  let prefecture = "";
  let prefectureIndex = -1;
  japanesePrefectures.forEach((candidate) => {
    const index = text.indexOf(candidate);
    if (index >= 0 && (prefectureIndex < 0 || index < prefectureIndex)) {
      prefecture = candidate;
      prefectureIndex = index;
    }
  });
  if (prefecture) {
    text = normalizeJapaneseAddressText(text.slice(prefectureIndex + prefecture.length));
  }

  let city = "";
  const cityMatch = text.match(/^([^0-9]+?[市区町村])\s*(.*)$/);
  if (cityMatch) {
    city = normalizeJapaneseAddressText(cityMatch[1]);
    text = normalizeJapaneseAddressText(cityMatch[2]);
  }

  let { street, building } = splitJapaneseStreetAndBuilding(text);
  const postalFallback = japanesePostalCodeFallbacks[postalCode];
  if (postalFallback && (!prefecture || !city)) {
    prefecture = prefecture || postalFallback.prefecture;
    city = city || postalFallback.city;
    street = street || postalFallback.street;
  }

  const normalizedAddress = buildJapaneseAddressDetail({ postalCode, prefecture, city, street, building });
  return {
    postalCode,
    prefecture,
    city,
    street,
    building,
    normalizedAddress,
    confidence: prefecture && city && street ? "medium" : "low",
  };
}

async function lookupJapanesePostalCodeAddress(postalCode) {
  const normalizedPostalCode = normalizeJapanesePostalCodeInput(postalCode);
  const digits = normalizedPostalCode.replace(/\D/g, "");
  if (digits.length !== 7) return null;
  const response = await axios.get(JAPANESE_POSTAL_CODE_LOOKUP_API, {
    params: { zipcode: digits },
  });
  const item = response.data?.results?.[0];
  if (!item) return null;
  return {
    postal_code: `${digits.slice(0, 3)}-${digits.slice(3)}`,
    prefecture: item.address1 || "",
    city: item.address2 || "",
    street: item.address3 || "",
    building: "",
    normalized_address: `${digits.slice(0, 3)}-${digits.slice(3)} ${item.address1 || ""}${item.address2 || ""}${item.address3 || ""}`,
    confidence: item.address1 && item.address2 ? "medium" : "low",
    source: "postal_code",
  };
}

async function safeLookupJapanesePostalCodeAddress(postalCode) {
  try {
    return await lookupJapanesePostalCodeAddress(postalCode);
  } catch (error) {
    return null;
  }
}

async function parseBookingAddressWithFallback(payload) {
  const postalCode = extractJapanesePostalCode(payload?.text);
  const localPostalOnly = postalCode && normalizeJapaneseAddressText(payload?.text).replace(/\D/g, "") === postalCode.replace(/\D/g, "");
  if (localPostalOnly) {
    const postalResult = await safeLookupJapanesePostalCodeAddress(postalCode);
    if (postalResult) return postalResult;
  }
  try {
    const response = await axios.post(`${API}/public-booking/address/parse`, payload);
    const data = response.data || {};
    if (postalCode && !(data.prefecture && data.city)) {
      const postalResult = await safeLookupJapanesePostalCodeAddress(postalCode);
      if (postalResult) return { ...data, ...postalResult };
    }
    return data;
  } catch (error) {
    if (
      typeof window !== "undefined" &&
      ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
      PUBLIC_ADDRESS_PARSE_FALLBACK_API &&
      !API.startsWith(PUBLIC_ADDRESS_PARSE_FALLBACK_API)
    ) {
      try {
        const response = await axios.post(`${PUBLIC_ADDRESS_PARSE_FALLBACK_API}/public-booking/address/parse`, payload);
        const data = response.data || {};
        if (postalCode && !(data.prefecture && data.city)) {
          const postalResult = await safeLookupJapanesePostalCodeAddress(postalCode);
          if (postalResult) return { ...data, ...postalResult };
        }
        return data;
      } catch (fallbackError) {
        if (postalCode) {
          const postalResult = await safeLookupJapanesePostalCodeAddress(postalCode);
          if (postalResult) return postalResult;
        }
        throw fallbackError;
      }
    }
    if (postalCode) {
      const postalResult = await safeLookupJapanesePostalCodeAddress(postalCode);
      if (postalResult) return postalResult;
    }
    throw error;
  }
}

function JapaneseAddressAssistField({ role, label, form, updateMany, placeholder }) {
  const map = japaneseAddressFieldMap[role];
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const value = form[map.location] || "";
  const parts = {
    postalCode: form[map.postalCode] || "",
    prefecture: form[map.prefecture] || "",
    city: form[map.city] || "",
    street: form[map.street] || "",
    building: form[map.building] || "",
  };

  const applyParts = (nextParts) => {
    const detail = buildJapaneseAddressDetail(nextParts);
    updateMany({
      [map.postalCode]: nextParts.postalCode,
      [map.prefecture]: nextParts.prefecture,
      [map.city]: nextParts.city,
      [map.street]: nextParts.street,
      [map.building]: nextParts.building,
      [map.detail]: detail,
      [map.location]: detail || value,
    });
  };

  const updatePart = (key, nextValue) => {
    applyParts({ ...parts, [key]: nextValue });
  };

  const autoParseJapanesePostalCode = async (postalCode, currentParts) => {
    const normalizedPostalCode = normalizeJapanesePostalCodeInput(postalCode);
    if (!/^\d{3}-\d{4}$/.test(normalizedPostalCode)) {
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const data = await parseBookingAddressWithFallback({
        text: normalizedPostalCode,
        lang: "ja",
        location_role: role,
      });
      const nextParts = {
        postalCode: data.postal_code || normalizedPostalCode,
        prefecture: data.prefecture || currentParts.prefecture || "",
        city: data.city || currentParts.city || "",
        street: data.street || currentParts.street || "",
        building: currentParts.building || data.building || "",
      };
      applyParts(nextParts);
      setStatus("success");
      setMessage("郵便番号から住所を自動入力しました。番地・建物名をご確認ください。");
    } catch (error) {
      const localResult = parseJapaneseAddressLocally(normalizedPostalCode);
      if (localResult.normalizedAddress && localResult.confidence !== "low") {
        applyParts({
          postalCode: localResult.postalCode || normalizedPostalCode,
          prefecture: localResult.prefecture || currentParts.prefecture || "",
          city: localResult.city || currentParts.city || "",
          street: localResult.street || currentParts.street || "",
          building: currentParts.building || localResult.building || "",
        });
        setStatus("success");
        setMessage("郵便番号から住所を自動入力しました。番地・建物名をご確認ください。");
        return;
      }
      setStatus("error");
      setMessage("郵便番号から住所を取得できませんでした。住所を手入力してください。");
    }
  };

  const parseAddress = async () => {
    const text = String(value || form[map.mapsUrl] || "").trim();
    if (!text) {
      setStatus("error");
      setMessage("住所またはGoogle Mapsリンクを入力してください。");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const data = await parseBookingAddressWithFallback({
        text,
        lang: "ja",
        location_role: role,
      });
      const nextParts = {
        postalCode: data.postal_code || "",
        prefecture: data.prefecture || "",
        city: data.city || "",
        street: data.street || "",
        building: data.building || "",
      };
      applyParts(nextParts);
      setStatus("success");
      setMessage(data.confidence === "low"
        ? "一部のみ自動入力しました。不足している項目をご確認ください。"
        : "住所情報を自動入力しました。内容をご確認ください。");
    } catch (error) {
      const localResult = parseJapaneseAddressLocally(text);
      if (localResult.normalizedAddress && localResult.confidence !== "low") {
        applyParts({
          postalCode: localResult.postalCode || "",
          prefecture: localResult.prefecture || "",
          city: localResult.city || "",
          street: localResult.street || "",
          building: localResult.building || "",
        });
        setStatus("success");
        setMessage("ローカル解析で住所情報を自動入力しました。内容をご確認ください。");
        return;
      }
      setStatus("error");
      setMessage("自動入力できませんでした。住所を手入力してください。");
    }
  };

  return (
    <div className="md:col-span-2 rounded-[20px] border border-white/10 bg-black/20 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="min-w-0 flex-1">
          <span className="mb-2 block text-sm font-semibold text-white/62">{label}</span>
          <input
            className="booking-input"
            value={value}
            onChange={(event) => {
              updateMany({
                [map.location]: event.target.value,
                [map.mapsUrl]: event.target.value,
              });
              setStatus("idle");
              setMessage("");
            }}
            placeholder={placeholder}
          />
        </label>
        <button
          type="button"
          onClick={parseAddress}
          disabled={status === "loading"}
          className="inline-flex h-[52px] shrink-0 items-center justify-center rounded-[16px] border border-[#d8b769]/35 bg-[#d8b769]/12 px-4 text-sm font-bold text-[#f3d98b] transition hover:bg-[#d8b769]/20 disabled:opacity-50"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          {status === "loading" ? "解析中..." : "住所を自動入力"}
        </button>
      </div>
      <p className="mt-2 text-xs leading-5 text-white/42">
        Google Mapsリンク、住所、郵便番号のみでも住所を自動入力できます。
      </p>
      {message && (
        <p className={`mt-2 text-xs font-semibold ${status === "error" ? "text-red-200" : "text-[#f3d98b]"}`}>
          {message}
        </p>
      )}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <JapaneseAddressPartInput
          label="郵便番号"
          value={parts.postalCode}
          onChange={(next) => {
            const normalizedPostalCode = normalizeJapanesePostalCodeInput(next);
            const nextParts = { ...parts, postalCode: normalizedPostalCode };
            applyParts(nextParts);
            if (/^\d{3}-\d{4}$/.test(normalizedPostalCode)) {
              autoParseJapanesePostalCode(normalizedPostalCode, nextParts);
            }
          }}
          placeholder="100-0005"
        />
        <JapaneseAddressPartInput label="都道府県" value={parts.prefecture} onChange={(next) => updatePart("prefecture", next)} placeholder="東京都" />
        <JapaneseAddressPartInput label="市区町村" value={parts.city} onChange={(next) => updatePart("city", next)} placeholder="千代田区" />
        <JapaneseAddressPartInput label="番地" value={parts.street} onChange={(next) => updatePart("street", next)} placeholder="丸の内1-9-1" />
        <JapaneseAddressPartInput label="建物名" value={parts.building} onChange={(next) => updatePart("building", next)} placeholder="ホテル名・建物名" />
      </div>
    </div>
  );
}

function JapaneseAddressPartInput({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold tracking-[0.08em] text-white/45">
        <MapPin className="h-3 w-3 text-[#d8b769]/70" />
        {label}
      </span>
      <input
        className="booking-input booking-input-compact"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function DarkField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-white/62">{label}</span>
      {children}
    </label>
  );
}

function OtherAreaWhatsAppPanel({ form, areaName, copy }) {
  const whatsappUrl = buildOtherAreaWhatsAppUrl(form, areaName, copy);
  return (
    <div className="mt-5 rounded-[24px] border border-[#d8b769]/35 bg-[#d8b769]/12 p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
            <MessageCircle className="h-6 w-6" />
          </span>
          <div>
            <p className="text-lg font-semibold text-white">{copy.otherArea.title}</p>
            <p className="mt-2 text-sm leading-6 text-white/62">
              {copy.otherArea.body}
            </p>
          </div>
        </div>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-[#d8b769] px-5 py-3 font-semibold text-black hover:brightness-105"
        >
          {copy.otherArea.button}
          <ArrowRight className="ml-2 h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

function FlightRecognitionPanel({
  form,
  airportCode,
  flightLookup,
  onAirportChange,
  onFlightNumberChange,
  onFlightTimeChange,
  onImportFlightDetails,
  lang,
  copy,
}) {
  const isDeparture = form.transfer_subtype === "departure";
  const timeLabel = isDeparture ? copy.flight.departureTime : copy.flight.landingTime;
  const airportLabel = isDeparture ? copy.flight.destinationAirport : copy.flight.pickupAirport;
  const recommendation = isDeparture ? departureRecommendation(airportCode, copy) : "";
  const canImportFlight = String(form.flight_number || "").trim().replace(/\s+/g, "").length >= 3;
  const isImporting = flightLookup?.status === "loading";
  return (
    <div className="rounded-[18px] border border-[#d8b769]/18 bg-black/22 p-3 shadow-inner shadow-white/[0.025]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#d8b769]/35 bg-[#d8b769]/12 text-[#f3d98b]">
            <Plane className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#d8b769]/88">{copy.flight.panelEyebrow}</p>
            <h3 className="truncate text-sm font-semibold text-white sm:text-base">
              {isDeparture ? copy.flight.importDestination : copy.flight.importArrival}
            </h3>
          </div>
        </div>
        <div className="w-fit shrink-0 rounded-full border border-white/10 bg-white/[0.045] px-2.5 py-1 text-[10px] font-semibold text-white/58">
          {airportCode ? `${copy.flight.detected}: ${airportDisplayName(airportCode, lang)}` : copy.flight.airportPending}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_7.25rem] gap-2.5 lg:grid-cols-[minmax(0,1fr)_9.5rem]">
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-semibold text-white/55">{copy.flight.flightNumber}</span>
          <div className="grid grid-cols-[minmax(0,1fr)_2.75rem] gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-2">
            <input
              className="booking-input booking-input-compact"
              value={form.flight_number}
              onChange={(e) => onFlightNumberChange(e.target.value.toUpperCase())}
              placeholder={copy.flight.example}
            />
            <button
              type="button"
              onClick={onImportFlightDetails}
              disabled={!canImportFlight || isImporting}
              aria-label={isImporting ? copy.flight.importing : copy.flight.importButton}
              className="inline-flex h-11 w-11 items-center justify-center whitespace-nowrap rounded-[13px] bg-[#d8b769] px-0 text-[11px] font-bold text-black shadow-md shadow-[#d8b769]/15 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto sm:px-3.5 sm:text-xs"
            >
              <Plane className="h-3.5 w-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">{isImporting ? copy.flight.importing : copy.flight.importButton}</span>
            </button>
          </div>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-semibold text-white/55">{timeLabel}</span>
          <input
            className="booking-input booking-input-compact"
            type="text"
            inputMode="numeric"
            value={form.flight_service_time}
            onChange={(e) => onFlightTimeChange(normalizeTimeInput(e.target.value) || e.target.value)}
            onBlur={(e) => onFlightTimeChange(normalizeTimeInput(e.target.value) || "")}
            placeholder="13:45"
          />
        </label>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2 rounded-[14px] border border-white/10 bg-white/[0.035] px-2.5 py-2">
        <p className="shrink-0 text-[11px] font-semibold text-white/52">{airportLabel}</p>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          {["narita_airport", "haneda_airport", "kansai_airport", "itami_airport"].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => onAirportChange(code)}
              className={`rounded-full border px-2.5 py-1.5 text-center text-[11px] font-semibold transition ${
                airportCode === code
                  ? "border-[#d8b769] bg-[#d8b769] text-black"
                  : "border-white/10 bg-black/25 text-white/62 hover:border-[#d8b769]/45 hover:text-white"
              }`}
            >
              {airportDisplayName(code, lang)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-2.5 rounded-[14px] border border-white/10 bg-black/25 px-2.5 py-2 text-[11px] leading-5 text-white/62 sm:text-xs">
        {form.flight_number ? (
          <FlightLookupStatus
            lookup={flightLookup}
            form={form}
            isDeparture={isDeparture}
            recommendation={recommendation}
            copy={copy}
          />
        ) : (
          copy.flight.empty
        )}
      </div>
    </div>
  );
}

function FlightLookupStatus({ lookup, form, isDeparture, recommendation, copy }) {
  if (lookup?.status === "loading") {
    return (
      <div className="flex items-center gap-2 text-white/72">
        <span className="h-2 w-2 animate-pulse rounded-full bg-[#f3d98b]" />
        {copy.flight.checking}
      </div>
    );
  }

  if (lookup?.status === "found") {
    const timeLabel = isDeparture ? copy.flight.departure : copy.flight.arrival;
    const isVerified = Boolean(lookup.selected_airport_area && lookup.selected_time_hhmm);
    const autoFilledItems = [
      lookup.selected_airport_area ? copy.flight.airport : null,
      lookup.selected_date_jst ? copy.flight.flightDate : null,
      lookup.selected_time_hhmm ? timeLabel.toLowerCase() : null,
      lookup.suggested_pickup_time_hhmm ? copy.flight.pickupTime : null,
    ].filter(Boolean);
    return (
      <div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-[#d8b769] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-black">{copy.flight.found}</span>
          <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] ${
            isVerified
              ? "border-emerald-300/30 bg-emerald-400/12 text-emerald-100"
              : "border-amber-300/30 bg-amber-400/12 text-amber-100"
          }`}>
            {isVerified ? copy.flight.verified : copy.flight.needsManualCheck}
          </span>
          <span className="text-xs font-semibold text-white sm:text-sm">{lookup.flight_number || form.flight_number}</span>
          {lookup.airline && <span className="text-white/50">{lookup.airline}</span>}
        </div>
        {autoFilledItems.length > 0 && (
          <p className="mt-1.5 rounded-[12px] border border-[#d8b769]/18 bg-[#d8b769]/8 px-2.5 py-1.5 text-[11px] font-semibold text-[#f3d98b]">
            {copy.flight.autoFilled}: {autoFilledItems.join(", ")}.
          </p>
        )}
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
          <FlightInfoLine label={copy.flight.from} value={[lookup.departure_airport, lookup.departure_iata].filter(Boolean).join(" ")} />
          <FlightInfoLine label={copy.flight.to} value={[lookup.arrival_airport, lookup.arrival_iata].filter(Boolean).join(" ")} />
          <FlightInfoLine label={copy.flight.date} value={lookup.selected_date_jst || form.departure_date || copy.flight.manualConfirm} />
          <FlightInfoLine label={timeLabel} value={lookup.selected_time_hhmm || form.flight_service_time || copy.flight.manualConfirm} />
          <FlightInfoLine label={copy.flight.pickupTime} value={lookup.suggested_pickup_time_hhmm || form.pickup_time || copy.flight.manualConfirm} />
        </div>
        <p className="mt-2 text-[11px] font-semibold text-[#f3d98b] sm:text-xs">{copy.flight.confirm}</p>
        {recommendation && (
          <p className="mt-1 text-[11px] font-semibold text-[#f3d98b] sm:text-xs">{recommendation}</p>
        )}
      </div>
    );
  }

  if (lookup?.status === "needs_configuration" || lookup?.status === "error") {
    return (
      <div>
        <span className="mb-2 inline-flex rounded-full border border-amber-300/30 bg-amber-400/12 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-amber-100">
          {copy.flight.needsManualCheck}
        </span>
        <br />
        <span className="font-semibold text-[#f3d98b]">{copy.flight.notConnected}</span>{" "}
        {copy.flight.manualTime}
        {recommendation && (
          <span className="mt-2 block text-[#f3d98b]">{recommendation}</span>
        )}
      </div>
    );
  }

  if (lookup?.status === "not_found" || lookup?.status === "invalid") {
    return (
      <div>
        <span className="mb-2 inline-flex rounded-full border border-amber-300/30 bg-amber-400/12 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-amber-100">
          {copy.flight.needsManualCheck}
        </span>
        <br />
        <span className="font-semibold text-[#f3d98b]">{copy.flight.notFound}</span>{" "}
        {copy.flight.checkManual}
        {recommendation && (
          <span className="mt-2 block text-[#f3d98b]">{recommendation}</span>
        )}
      </div>
    );
  }

  return (
    <>
      <span className="font-semibold text-[#f3d98b]">{copy.flight.time}:</span>{" "}
      {form.flight_service_time || copy.flight.empty}
      {recommendation && (
        <span className="mt-2 block text-[#f3d98b]">{recommendation}</span>
      )}
    </>
  );
}

function FlightInfoLine({ label, value }) {
  return (
    <div className="rounded-[11px] border border-white/10 bg-white/[0.035] px-2.5 py-2">
      <p className="text-[8px] font-semibold uppercase tracking-[0.12em] text-white/35">{label}</p>
      <p className="mt-0.5 text-[11px] font-semibold leading-4 text-white sm:text-xs">{value || "-"}</p>
    </div>
  );
}

function DateCalendarPicker({ value, onChange, lang, copy }) {
  const today = useMemo(() => startOfToday(), []);
  const selectedDate = useMemo(() => parseDateObject(value, today), [value, today]);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => firstDayOfMonth(selectedDate));
  const calendarDays = useMemo(() => buildCalendarDays(viewDate, today), [viewDate, today]);
  const locale = localeForLang(lang);

  useEffect(() => {
    if (!value) {
      onChange(toDateInputValue(today));
    }
  }, [value, today, onChange]);

  useEffect(() => {
    if (open) {
      setViewDate(firstDayOfMonth(selectedDate));
    }
  }, [open, selectedDate]);

  const selectedLabel = formatDateLong(selectedDate, locale);
  const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(viewDate);
  const canGoPrevious = !isSameMonth(viewDate, today);

  const selectDate = (date) => {
    if (isBeforeDay(date, today)) return;
    onChange(toDateInputValue(date));
    setOpen(false);
  };

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-white/62">{copy.fields.date}</p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-[22px] border border-white/10 bg-white/[0.04] p-4 text-left transition hover:border-[#d8b769]/45 hover:bg-white/[0.07]"
      >
        <span>
          <span className="block text-lg font-semibold text-white">{selectedLabel}</span>
          <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.22em] text-[#f3d98b]/75">
            {copy.notes.jst}
          </span>
        </span>
        <CalendarClock className="h-5 w-5 text-[#d8b769]" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/72 p-3 backdrop-blur-sm sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label={copy.datePicker.selectTripDate}
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-[30px] border border-[#d8b769]/30 bg-[#090806] p-5 shadow-2xl shadow-black/70"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => canGoPrevious && setViewDate(addMonths(viewDate, -1))}
                disabled={!canGoPrevious}
                className={`flex h-11 w-11 items-center justify-center rounded-full border transition ${
                  canGoPrevious
                    ? "border-white/10 text-white/75 hover:border-[#d8b769]/50 hover:text-[#f3d98b]"
                    : "border-white/5 text-white/18"
                }`}
                aria-label={copy.datePicker.previousMonth}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f3d98b]/70">{copy.datePicker.selectDate}</p>
                <p className="mt-1 text-xl font-semibold text-white">{monthLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setViewDate(addMonths(viewDate, 1))}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white/75 transition hover:border-[#d8b769]/50 hover:text-[#f3d98b]"
                aria-label={copy.datePicker.nextMonth}
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-7 gap-2 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-white/36">
              {weekdayLabels(locale).map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-7 gap-2">
              {calendarDays.map(({ date, inMonth }, index) => {
                const disabled = !inMonth || isBeforeDay(date, today);
                const selected = isSameDay(date, selectedDate);
                const isToday = isSameDay(date, today);
                return (
                  <button
                    key={`${toDateInputValue(date)}-${index}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => selectDate(date)}
                    className={`relative flex aspect-square items-center justify-center rounded-2xl border text-sm font-semibold transition ${
                      selected
                        ? "border-[#f3d98b] bg-[#d8b769] text-black shadow-lg shadow-[#d8b769]/25"
                        : disabled
                          ? "border-transparent text-white/14"
                          : "border-white/8 bg-white/[0.035] text-white/72 hover:border-[#d8b769]/45 hover:bg-[#d8b769]/12 hover:text-[#f3d98b]"
                    }`}
                  >
                    {date.getDate()}
                    {isToday && !selected && !disabled && (
                      <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-[#d8b769]" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 border-t border-white/10 pt-4">
              <div>
                <p className="text-xs text-white/40">{copy.datePicker.selected}</p>
                <p className="text-sm font-semibold text-white">{selectedLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-2xl bg-[#d8b769] px-5 py-3 text-sm font-bold text-black shadow-lg shadow-[#d8b769]/20"
              >
                {copy.datePicker.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimeWheelPicker({ label, note, value, onChange, copy = publicBookingCopy.en }) {
  const displayLabel = label || copy.fields.time;
  const displayNote = note || copy.notes.timeFormat;

  const fallback = roundTimeToNextStep();
  const timeValue = value || fallback;
  const selectedMinutes = parseTimeValue(normalizeTimeInput(timeValue)) ?? parseTimeValue(fallback) ?? 0;
  const selectedHour = Math.floor(selectedMinutes / 60);
  const selectedMinute = selectedMinutes % 60;
  const [open, setOpen] = useState(false);
  const badgeText = open ? copy.datePicker.confirm : copy.notes.timeFormat;
  const hourWheelRef = useRef(null);
  const minuteWheelRef = useRef(null);
  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, index) => index), []);
  const minuteOptions = useMemo(() => Array.from({ length: 60 }, (_, index) => index), []);

  const setTimeParts = (hour, minute) => {
    onChange(formatMinutesAsTime((hour * 60) + minute));
  };

  useEffect(() => {
    if (!value) {
      onChange(fallback);
    }
  }, [value, fallback, onChange]);

  useEffect(() => {
    if (!open) return;
    const alignWheels = () => {
      [
        { ref: hourWheelRef, value: selectedHour },
        { ref: minuteWheelRef, value: selectedMinute },
      ].forEach(({ ref, value }) => {
        const container = ref.current;
        const item = container?.querySelector(`[data-time-value="${value}"]`);
        if (!container || !item) return;
        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        container.scrollTop += itemRect.top - containerRect.top - ((containerRect.height - itemRect.height) / 2);
      });
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(alignWheels));
    const timer = window.setTimeout(alignWheels, 80);
    return () => window.clearTimeout(timer);
  }, [open, selectedHour, selectedMinute]);

  return (
    <div className="relative">
      <p className="mb-2 text-sm font-semibold text-white/62">{displayLabel}</p>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex w-full items-center justify-between rounded-[18px] border px-3 py-3 text-left transition ${
          open
            ? "border-[#d8b769]/70 bg-[#d8b769]/10 shadow-lg shadow-[#d8b769]/10"
            : "border-white/10 bg-white/[0.04] hover:border-[#d8b769]/40 hover:bg-white/[0.06]"
        }`}
        aria-expanded={open}
        aria-label={displayLabel}
      >
        <span className="flex min-w-0 items-center gap-2">
          <CalendarClock className="h-4 w-4 shrink-0 text-[#d8b769]" />
          <span className="text-xl font-semibold tracking-[0.05em] text-white sm:text-2xl">{timeValue}</span>
        </span>
        <span className="ml-2 shrink-0 rounded-full border border-white/10 bg-black/22 px-2.5 py-1 text-[10px] font-bold text-[#f3d98b]/72 sm:px-3">
          {badgeText}
        </span>
      </button>
      <div className="px-1">
        <p className="mt-1.5 break-words text-[10.5px] font-semibold leading-4 text-[#f3d98b]/68 sm:text-[11px]">
          {displayNote}
        </p>
      </div>
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 rounded-[22px] border border-[#d8b769]/28 bg-[#0b0a08] p-3 shadow-2xl shadow-black/60">
          <div className="pointer-events-none absolute inset-x-3 top-3 h-8 rounded-t-[16px] bg-gradient-to-b from-[#0b0a08] to-transparent" />
          <div className="pointer-events-none absolute inset-x-3 bottom-3 h-8 rounded-b-[16px] bg-gradient-to-t from-[#0b0a08] to-transparent" />
          <div className="grid grid-cols-2 gap-2">
            <MiniTimeWheelColumn
              label="HH"
              options={hourOptions}
              value={selectedHour}
              wheelRef={hourWheelRef}
              onSelect={(hour) => setTimeParts(hour, selectedMinute)}
            />
            <MiniTimeWheelColumn
              label="MM"
              options={minuteOptions}
              value={selectedMinute}
              wheelRef={minuteWheelRef}
              onSelect={(minute) => setTimeParts(selectedHour, minute)}
            />
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex h-9 w-full items-center justify-center rounded-[14px] bg-[#d8b769] text-xs font-bold text-black shadow-md shadow-[#d8b769]/15 transition hover:brightness-110"
          >
            {copy.datePicker.confirm}
          </button>
        </div>
      )}
    </div>
  );
}

function MiniTimeWheelColumn({ label, options, value, onSelect, wheelRef }) {
  return (
    <div className="overflow-hidden rounded-[16px] border border-white/10 bg-black/26">
      <p className="border-b border-white/10 py-1.5 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-[#f3d98b]/62">
        {label}
      </p>
      <div ref={wheelRef} className="mini-time-wheel h-32 overflow-y-auto px-1 py-12">
        {options.map((option) => {
          const selected = option === value;
          return (
            <button
              key={option}
              type="button"
              data-time-value={option}
              onClick={() => onSelect(option)}
              className={`block h-8 w-full rounded-[12px] text-center text-sm font-semibold tracking-[0.08em] transition ${
                selected
                  ? "bg-[#d8b769] text-black shadow-sm shadow-[#d8b769]/25"
                  : "text-white/52 hover:bg-white/[0.07] hover:text-white"
              }`}
            >
              {String(option).padStart(2, "0")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function parseDateObject(value, today) {
  if (!value) return new Date(today);
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  if (!Number.isFinite(parsed.getTime())) return new Date(today);
  return isBeforeDay(parsed, today) ? new Date(today) : parsed;
}

function firstDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function isSameMonth(left, right) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
}

function isSameDay(left, right) {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function isBeforeDay(left, right) {
  const leftDate = new Date(left.getFullYear(), left.getMonth(), left.getDate());
  const rightDate = new Date(right.getFullYear(), right.getMonth(), right.getDate());
  return leftDate.getTime() < rightDate.getTime();
}

function buildCalendarDays(viewDate) {
  const first = firstDayOfMonth(viewDate);
  const leadingDays = first.getDay();
  const totalDays = daysInMonth(viewDate.getFullYear(), viewDate.getMonth() + 1);
  const cells = [];
  for (let index = 0; index < 42; index += 1) {
    const dayOffset = index - leadingDays + 1;
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), dayOffset);
    const inMonth = dayOffset >= 1 && dayOffset <= totalDays;
    cells.push({ date, inMonth });
  }
  return cells;
}

function localeForLang(lang) {
  if (lang === "ja") return "ja-JP";
  if (lang === "zhHant") return "zh-Hant";
  if (lang === "zh") return "zh-CN";
  return "en-US";
}

function weekdayLabels(locale) {
  const sunday = new Date(2026, 5, 14);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(sunday);
    date.setDate(sunday.getDate() + index);
    return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);
  });
}

function formatDateLong(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function roundTimeToNextStep() {
  const now = new Date();
  const minute = now.getMinutes();
  const nextMinute = Math.ceil(minute / 5) * 5;
  if (nextMinute >= 60) {
    now.setHours(now.getHours() + 1, 0, 0, 0);
  } else {
    now.setMinutes(nextMinute, 0, 0);
  }
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

function normalizeTimeInput(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.includes(":")) {
    const [hourPart, minutePart = ""] = raw.split(":");
    const hour = Math.min(23, Number(String(hourPart).replace(/\D/g, "") || 0));
    const minute = Math.min(59, Number(String(minutePart).replace(/\D/g, "").padEnd(2, "0") || 0));
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  const hourDigits = digits.length <= 2 ? digits : digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2);
  const minuteDigits = digits.length <= 2 ? "00" : digits.length === 3 ? digits.slice(1, 3) : digits.slice(2, 4);
  const hour = Math.min(23, Number(hourDigits || 0));
  const minute = Math.min(59, Number(minuteDigits.padEnd(2, "0") || 0));
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function parseTimeValue(value) {
  const match = String(value || "").match(/^(\d{1,2}):?(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

function formatMinutesAsTime(totalMinutes) {
  const dayMinutes = 24 * 60;
  const normalized = ((totalMinutes % dayMinutes) + dayMinutes) % dayMinutes;
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function shiftTimeValue(value, deltaMinutes) {
  const parsed = parseTimeValue(normalizeTimeInput(value));
  if (parsed == null) return roundTimeToNextStep();
  return formatMinutesAsTime(parsed + deltaMinutes);
}

function WizardNav({ activeStep, loading, whatsappUrl, onBack, onNext, copy }) {
  if (activeStep >= steps.length - 1) return null;
  const isFirstStep = activeStep === 0;
  return (
    <div className={`mt-6 flex items-center gap-2 border-t border-white/10 pt-4 ${isFirstStep ? "justify-end" : "justify-between"}`}>
      {!isFirstStep && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-11 min-w-0 flex-1 items-center justify-center rounded-[16px] border border-white/12 bg-white/[0.025] px-4 text-sm font-semibold text-white/72 transition hover:border-white/24 hover:bg-white/[0.06]"
        >
          <ArrowLeft className="mr-2 h-4 w-4 shrink-0" />
          {copy.nav.back}
        </button>
      )}
      {whatsappUrl ? (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-11 min-w-0 flex-[1.45] items-center justify-center rounded-[16px] bg-[#d8b769] px-4 text-sm font-semibold text-black transition hover:brightness-105"
        >
          <span className="truncate">{copy.nav.whatsapp}</span>
          <MessageCircle className="ml-2 h-4 w-4 shrink-0" />
        </a>
      ) : (
        <button
          type="button"
          onClick={onNext}
          disabled={loading}
          className={`inline-flex h-11 min-w-0 items-center justify-center rounded-[16px] bg-[#f0cd69] px-5 text-sm font-bold text-black shadow-md shadow-[#d8b769]/10 transition hover:brightness-110 disabled:opacity-50 ${isFirstStep ? "w-full" : "flex-[1.45]"}`}
        >
          <span className="truncate">{loading ? copy.nav.preparing : activeStep === 3 ? copy.nav.reviewBooking : copy.nav.continue}</span>
          <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
        </button>
      )}
    </div>
  );
}

function buildOtherAreaWhatsAppUrl(form, areaName, copy = publicBookingCopy.en) {
  const service = form.service_type === "charter" ? copy.charterTitle : copy.serviceTypes.fallback;
  const message = [
    copy === publicBookingCopy.ja ? "湘南国際株式会社 ご担当者様" : "Hello Shonan International,",
    "",
    copy.otherArea.title,
    "",
    `${copy.summary.service}: ${service}`,
    `${copy.summary.vehicle}: ${form.vehicle_type}`,
    `${copy.review.rows.dateTime}: ${form.departure_date || "-"} ${form.pickup_time || ""} ${copy.notes.jst}`,
    `${copy.fields.charterDepartureArea}: ${areaName(form.pickup_area)}`,
    `${copy.fields.charterCityArea}: ${areaName(form.dropoff_area)}`,
    `${copy.fields.pickupLocation}: ${form.pickup_location || ""}`,
    `${copy.fields.dropoffLocation}: ${form.destination || ""}`,
    `${copy.passengers}: ${form.passenger_count}`,
    `${copy.luggage}: ${form.luggage_count}`,
    "",
    copy.quoteFallback,
  ].join("\n");
  const phone = PUBLIC_BOOKING_WHATSAPP_PHONE.replace(/\D/g, "");
  const base = phone ? `https://wa.me/${phone}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(message)}`;
}

function buildPaymentWhatsAppUrl(result, copy = publicBookingCopy.en) {
  const message = [
    copy === publicBookingCopy.ja ? "湘南国際株式会社 ご担当者様" : "Hello Shonan International,",
    "",
    copy.result.whatsappPayment,
    "",
    `${copy.result.title}: ${result.confirmation_code || ""}`,
    `${copy.result.openPayment}: ${result.payment_url || ""}`,
    "",
    copy === publicBookingCopy.ja ? "よろしくお願いいたします。" : "Thank you.",
  ].join("\n");
  const phone = PUBLIC_BOOKING_WHATSAPP_PHONE.replace(/\D/g, "");
  const base = phone ? `https://wa.me/${phone}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(message)}`;
}

function SummaryPanel({ form, quote, quoteError, areaName, activeStep, copy }) {
  const total = quote?.total_price;
  const serviceLabel = form.service_type === "charter" ? copy.charterTitle : transferSubtypeLabel(form.transfer_subtype, copy);
  const vehicleLabel = form.vehicle_type || copy.summary.chooseVehicle;
  const reachedSteps = new Set(steps.slice(0, activeStep + 1).map((step) => step.key));
  const showTrip = reachedSteps.has("trip");
  const showVehicle = reachedSteps.has("vehicle");
  const showContact = reachedSteps.has("contact");
  const timeValue = form.departure_date
    ? `${form.departure_date} ${form.pickup_time || ""} ${copy.notes.jst}`
    : copy.summary.chooseDateTime;
  const routeValue = buildRouteSummary(form, areaName, copy);
  const flightLine = buildFlightSummary(form, copy);
  return (
    <section className="rounded-[28px] border border-[#d8b769]/22 bg-[#11100d] p-5 shadow-2xl shadow-black/30">
      <div className="flex items-center gap-3">
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#d8b769]/12 text-[#d8b769]">
          <Briefcase className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#d8b769]">{copy.summary.eyebrow}</p>
          <h2 className="text-xl font-semibold text-white">{copy.summary.title}</h2>
        </div>
      </div>
      <div className="mt-6 space-y-4 text-sm text-white/65">
        <SummaryItem icon={<Crown className="h-4 w-4" />} label={copy.summary.service} value={serviceLabel} />
        {showVehicle && (
          <>
            <SummaryItem icon={<Car className="h-4 w-4" />} label={copy.summary.vehicle} value={vehicleLabel} />
            <SummaryItem icon={<UserRound className="h-4 w-4" />} label={copy.summary.guests} value={`${form.passenger_count} ${copy.summary.passengerUnit} · ${form.luggage_count} ${copy.summary.luggageUnit}`} />
            <SummaryItem icon={<ShieldCheck className="h-4 w-4" />} label={copy.summary.childSeats} value={`${form.child_seat_count} ${copy.summary.childSeatValue}`} />
            <SummaryItem icon={<ShieldCheck className="h-4 w-4" />} label={copy.infantSeats} value={`${form.infant_seat_0_3_count || 0} · ¥${((form.infant_seat_0_3_count || 0) * 2000).toLocaleString()}`} />
          </>
        )}
        {showTrip && (
          <>
            <SummaryItem icon={<CalendarClock className="h-4 w-4" />} label={copy.summary.tripTime} value={timeValue} />
            <SummaryItem icon={<MapPin className="h-4 w-4" />} label={copy.summary.route} value={routeValue} />
            {form.service_type === "transfer" && form.transfer_subtype !== "point_to_point" && (
              <SummaryItem icon={<Plane className="h-4 w-4" />} label={copy.summary.flight} value={flightLine} />
            )}
          </>
        )}
        {showContact && (
          <SummaryItem icon={<Briefcase className="h-4 w-4" />} label={copy.summary.passenger} value={form.full_name || copy.summary.enterPassenger} />
        )}
      </div>
      <div className="mt-6 rounded-2xl border border-white/10 bg-black/35 p-4">
        <p className="text-xs uppercase tracking-[0.22em] text-white/35">{copy.summary.estimatedTotal}</p>
        {total ? (
          <p className="mt-2 text-4xl font-semibold text-[#f3d98b]">¥{Number(total).toLocaleString()}</p>
        ) : (
          <p className="mt-2 text-sm leading-6 text-white/55">{quoteError || (activeStep < 4 ? copy.summary.priceReview : copy.summary.pricePending)}</p>
        )}
      </div>
      <div className="mt-4 rounded-2xl bg-[#d8b769]/10 p-4 text-xs leading-6 text-[#f5deb0]">
        {copy.summary.urgent}
      </div>
    </section>
  );
}

function SummaryItem({ icon, label, value }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-[#d8b769]">{icon}</span>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-white/32">{label}</p>
        <p className="mt-1 font-semibold text-white/82">{value || "-"}</p>
      </div>
    </div>
  );
}

function ReviewRows({ form, quote, quoteError, areaName, copy }) {
  const rows = [
    [copy.review.rows.serviceType, form.service_type === "charter" ? copy.charterTitle : transferSubtypeLabel(form.transfer_subtype, copy)],
    [copy.review.rows.dateTime, `${form.departure_date || "-"} ${form.pickup_time || "-"} ${copy.notes.jst}`],
    [copy.review.rows.route, buildRouteSummary(form, areaName, copy)],
    ...(form.service_type === "transfer" && form.transfer_subtype !== "point_to_point" ? [[copy.review.rows.flight, buildFlightSummary(form, copy)]] : []),
    [copy.review.rows.vehicle, form.vehicle_type],
    [copy.review.rows.passengersLuggage, `${form.passenger_count} / ${form.luggage_count}`],
    [copy.review.rows.childSeats, `${form.child_seat_count} ${copy.review.childSeatsValue}`],
    [copy.infantSeats, `${form.infant_seat_0_3_count || 0} · ¥${((form.infant_seat_0_3_count || 0) * 2000).toLocaleString()}`],
    [copy.review.rows.finalAmount, quote?.total_price ? `¥${Number(quote.total_price).toLocaleString()} JPY` : quoteError || copy.review.pendingQuote],
  ];
  return (
    <div className="divide-y divide-white/10">
      {rows.map(([label, value]) => (
        <div key={label} className="grid gap-1 py-4 sm:grid-cols-[180px_1fr]">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/36">{label}</p>
          <p className="font-semibold text-white">{value}</p>
        </div>
      ))}
    </div>
  );
}

function PolicyCheck({ label, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/25 p-3 text-white/72">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="mt-1 h-4 w-4 accent-[#d8b769]" />
      <span>{label}</span>
    </label>
  );
}

function TurnstileVerification({ siteKey, onVerify, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey || !containerRef.current) return undefined;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current || widgetIdRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerify(token),
          "expired-callback": onExpire,
          "error-callback": onExpire,
        });
      })
      .catch(() => onExpire());

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, onVerify, onExpire]);

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-black/25 p-3">
      <div ref={containerRef} className="min-h-[65px]" />
    </div>
  );
}
